import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Badge } from '../ui/badge'
import { Checkbox } from '../ui/checkbox'
import { Combobox, MultiCombobox } from '../ui/combobox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import {
  Loader2,
  Calendar,
  Clock,
  Plus,
  ChevronLeft,
  ChevronRight,
  Settings,
  ClipboardList,
  MapPin,
  Briefcase,
  User,
  Users,
  X,
  MoreHorizontal,
  Edit,
  Trash2,
  Check,
  AlertTriangle,
  Search,
  LayoutGrid,
  List,
  Printer,
  ScanLine,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
  SlidersHorizontal,
  Wand2,
  UserPlus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Calendar as CalendarPicker } from '../ui/calendar'
import ScheduleEndpoints from '../../endpoints/ScheduleEndpoints'
import ReportEndpoints from '../../endpoints/ReportEndpoints'
import { ScheduleExportDialog } from './ScheduleExportDialog'
import { ScheduleEmailDialog } from './schedule/ScheduleEmailDialog'
import {
  BulkEmployeeEmailDialog,
  BulkEmailTarget,
} from './schedule/BulkEmployeeEmailDialog'
import EventEndpoints from '../../endpoints/EventEndpoints'
import IdentityEndpoints from '../../endpoints/IdentityEndpoints'
import { BadgePrintingLogic } from '../../common/BadgePrintingLogic'
import {
  EventSchedule,
  Shift,
  ShiftRecurrence,
  ShiftSpot,
  ShiftRecurrenceState,
  ShiftSpotState,
  ShiftSpotPunchClockState,
  IntervalTimeContext,
  PunchType,
  PunchTypeFriendlyName,
  PunchClock,
  PunchClockPunch,
} from '../../model/EventSchedule'
import { QuickProvisioningIdentity, SimpleIdentity } from '../../model/Identity'
import { Event, Location, Position } from '../../model/Event'
import { EventWeekHelper, Week } from '../../model/EventWeekHelper'
import { ShiftRequest } from '../../requests/ShiftRequest'
import { ShiftSpotRequest } from '../../requests/ShiftSpotRequest'
import { Separator } from '../ui/separator'
import { DatePicker } from '../ui/date-picker'
import { useSessionStore } from '../../stores/useSessionStore'
import { DateTime } from 'luxon'
import { toast } from 'sonner'
import { extractErrorMessage } from '../../common/extractErrorMessage'
import { SetupPrerequisiteAlert } from './setup/SetupPrerequisiteAlert'
import {
  computeEventSetupStatus,
  setupStepHref,
  wizardHref,
  SetupStepId,
} from './setup/eventSetupStatus'

// ─── Time helpers ────────────────────────────────────────────────────────

const formatTime = (timeStr: string): string => {
  if (!timeStr) return '-'
  try {
    return DateTime.fromFormat(timeStr, 'HH:mm:ss').toFormat('h:mm a')
  } catch {
    return timeStr
  }
}

const formatDateShort = (date: Date | string): string => {
  try {
    const d =
      typeof date === 'string'
        ? DateTime.fromISO(date)
        : DateTime.fromJSDate(date)
    return d.isValid ? d.toFormat('EEE MMM d') : '-'
  } catch {
    return '-'
  }
}

const timeOptions = (() => {
  const opts: { value: string; label: string }[] = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hStr = h.toString().padStart(2, '0')
      const mStr = m.toString().padStart(2, '0')
      const value = `${hStr}:${mStr}`
      const dt = DateTime.fromFormat(value, 'HH:mm')
      opts.push({ value, label: dt.toFormat('h:mm a') })
    }
  }
  return opts
})()

// ─── Punch clock color mapping ───────────────────────────────────────────

const punchClockSpotClass = (state?: ShiftSpotPunchClockState): string => {
  switch (state) {
    case ShiftSpotPunchClockState.CHECKED_IN:
      return 'bg-green-100 text-green-800 hover:bg-green-200'
    case ShiftSpotPunchClockState.BREAK:
      return 'bg-purple-100 text-purple-800 hover:bg-purple-200'
    case ShiftSpotPunchClockState.CHECKED_OUT:
      return 'bg-blue-100 text-blue-800 hover:bg-blue-200'
    case ShiftSpotPunchClockState.LATE:
    case ShiftSpotPunchClockState.MISSED:
      return 'bg-amber-100 text-amber-800 hover:bg-amber-200'
    case ShiftSpotPunchClockState.ERROR:
      return 'bg-rose-100 text-rose-800 hover:bg-rose-200'
    case ShiftSpotPunchClockState.PENDING:
    default:
      return 'bg-slate-100 text-slate-700 hover:bg-slate-200'
  }
}

// ─── Spot Status Indicators ──────────────────────────────────────────────

const RecurrenceStatusBadge: React.FC<{ state: ShiftRecurrenceState }> = ({
  state,
}) => {
  switch (state) {
    case ShiftRecurrenceState.FULLY_ASSIGNED:
      return <Check className="h-3.5 w-3.5 text-green-600" />
    case ShiftRecurrenceState.OVER_ASSIGNED:
      return <Check className="h-3.5 w-3.5 text-blue-600" />
    case ShiftRecurrenceState.UNDER_ASSIGNED:
      return <AlertTriangle className="h-3.5 w-3.5 text-yellow-600" />
    case ShiftRecurrenceState.UNASSIGNED:
      return <X className="h-3.5 w-3.5 text-gray-400" />
    default:
      return null
  }
}

// Format date for API query param (yyyy-MM-dd)
const formatDateForApi = (date: Date): string => {
  return DateTime.fromJSDate(date).toFormat('yyyy-MM-dd')
}

// Parse a date value (string or millis) as local time using Luxon
const parseLocalDate = (d: any): Date => {
  if (d instanceof Date) return d
  const s = String(d)
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return DateTime.fromISO(s).toJSDate()
  return DateTime.fromMillis(Number(d)).toJSDate()
}

// True when any spot in the payload came back without a real id. Spots created
// by a shift edit are serialized before their ids are generated on some backend
// builds, and selection/assignment are keyed by id — treating those spots as
// real makes them share one key, so they all check together and assignment
// fails with "shift spots do not exist".
const hasUnidentifiedSpot = (s: EventSchedule): boolean =>
  (s.shifts ?? []).some((shift) =>
    (shift.recurrences ?? []).some((r) =>
      (r.spots ?? []).some((sp) => !sp.id)
    )
  )

// Spot status filter values (mirrors legacy ShowShiftStatus enum).
const SPOT_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'All Spots' },
  { value: 'unfilled', label: 'Unfilled Only' },
  { value: 'filled', label: 'Assigned & Unavailable' },
  { value: 'assigned', label: 'Assigned Only' },
  { value: 'unassignable', label: 'Unavailable Only' },
]

const matchesSpotStatus = (spot: ShiftSpot, filter: string): boolean => {
  if (filter === 'all') return true
  if (filter === 'unfilled') return spot.state === ShiftSpotState.UNASSIGNED
  if (filter === 'filled')
    return (
      spot.state === ShiftSpotState.ASSIGNED ||
      spot.state === ShiftSpotState.UNASSIGNABLE
    )
  if (filter === 'assigned') return spot.state === ShiftSpotState.ASSIGNED
  if (filter === 'unassignable')
    return spot.state === ShiftSpotState.UNASSIGNABLE
  return true
}

// Format a HH:mm:ss-ish begin-time string for human display.
const formatStartTime = (t: string): string => {
  const parsed = DateTime.fromFormat(t, 'HH:mm:ss')
  if (parsed.isValid) return parsed.toFormat('h:mm a')
  const parsedShort = DateTime.fromFormat(t, 'HH:mm')
  if (parsedShort.isValid) return parsedShort.toFormat('h:mm a')
  return t
}

// ─── Main Component ──────────────────────────────────────────────────────

const LOAD_ALL_WEEKS_KEY = 'ems-schedule-load-all-weeks'

// How many shift rows the calendar renders before offering "Load more" —
// rendering is what makes week switching feel slow on big schedules, not data
const CALENDAR_SHIFTS_PAGE_SIZE = 25

// Plain string/number math for the per-recurrence hot paths: with every week
// loaded these run over the whole event on each week switch, and constructing
// Luxon DateTimes in the inner loops made that visibly laggy. Backend dates
// are always 'yyyy-MM-dd' strings and shift times 'HH:mm:ss'.
const timeToHours = (t: string): number => {
  const [h = '0', m = '0', s = '0'] = t.split(':')
  return Number(h) + Number(m) / 60 + Number(s) / 3600
}

const shiftDurationHours = (beginTime: string, endTime: string): number => {
  const begin = timeToHours(beginTime)
  const end = timeToHours(endTime)
  return begin < end ? end - begin : 24 - (begin - end)
}

export const EventScheduleModern: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>()
  const navigate = useNavigate()
  const { hasAdmin, hasScheduleAdminFor } = useSessionStore()

  const [schedule, setSchedule] = useState<EventSchedule | null>(null)
  const [event, setEvent] = useState<Event | null>(null)
  const [schedulableIdentities, setSchedulableIdentities] = useState<
    QuickProvisioningIdentity[]
  >([])
  const [loading, setLoading] = useState(true)
  const [weekLoading, setWeekLoading] = useState(false)
  const [switchingView, setSwitchingView] = useState(false)
  const [currentWeek, setCurrentWeek] = useState(0)
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')
  const [loadAllWeeks, setLoadAllWeeksState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LOAD_ALL_WEEKS_KEY) === 'true'
    } catch {
      return false
    }
  })

  const setLoadAllWeeks = (value: boolean) => {
    try {
      localStorage.setItem(LOAD_ALL_WEEKS_KEY, String(value))
    } catch {
      // Persistence is best-effort; the toggle still works for this session
    }
    setLoadAllWeeksState(value)
  }

  // Filters
  const [locationFilter, setLocationFilter] = useState<string>('all')
  // Positions are multi-select: ['all'] means no restriction, otherwise the
  // array holds the selected position ids as strings
  const [positionFilters, setPositionFilters] = useState<string[]>(['all'])
  const [startTimeFilter, setStartTimeFilter] = useState<string>('all')
  const [spotStatusFilter, setSpotStatusFilter] = useState<string>('all')
  const [identityFilter, setIdentityFilter] = useState<string>('all')
  const [onlyShiftsWithSpots, setOnlyShiftsWithSpots] = useState(false)
  const [visibleShiftCount, setVisibleShiftCount] = useState(
    CALENDAR_SHIFTS_PAGE_SIZE
  )
  const [employeeSearch, setEmployeeSearch] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [sidebarSearch, setSidebarSearch] = useState('')
  // 'any', or a yyyy-MM-dd date in the current week: show only employees
  // with no shift that day
  const [sidebarFreeOnDate, setSidebarFreeOnDate] = useState<string>('any')

  const resetFilters = () => {
    setLocationFilter('all')
    setPositionFilters(['all'])
    setStartTimeFilter('all')
    setSpotStatusFilter('all')
    setIdentityFilter('all')
    setOnlyShiftsWithSpots(false)
  }

  const changeViewMode = (mode: 'calendar' | 'list') => {
    if (mode === viewMode) return
    resetFilters()
    // Hide the previous view's shifts and show a spinner until the incoming
    // view's data finishes loading, so we never flash stale shifts. We keep
    // `schedule` populated (the rest of the page depends on it) and instead
    // gate the shift rendering on this flag, cleared when the reload lands.
    setSwitchingView(true)
    setViewMode(mode)
  }

  // Shift dialog
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false)
  const [editingShift, setEditingShift] = useState<Shift | null>(null)

  // Assign dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [bulkAssignSpotIds, setBulkAssignSpotIds] = useState<number[]>([])
  const [assignTarget, setAssignTarget] = useState<{
    spot: ShiftSpot
    shift: Shift
    recurrence: ShiftRecurrence
  } | null>(null)
  const [assignSearch, setAssignSearch] = useState('')
  // Hide employees who already have a shift on the target day(s). Kept across
  // dialog openings so it doesn't need re-ticking for every spot
  const [assignFreeOnly, setAssignFreeOnly] = useState(false)

  // Spot detail dialog
  const [spotDialogOpen, setSpotDialogOpen] = useState(false)
  const [spotDialogData, setSpotDialogData] = useState<{
    spot: ShiftSpot
    shift: Shift
    recurrence: ShiftRecurrence
  } | null>(null)

  // Multi-assign mode
  const [selectedIdentity, setSelectedIdentity] =
    useState<QuickProvisioningIdentity | null>(null)
  const [selectedSpotIds, setSelectedSpotIds] = useState<Set<number>>(new Set())
  const [bulkAssigning, setBulkAssigning] = useState(false)

  // Which setup steps are still outstanding, so empty states can name the real
  // blocker instead of pointing at "create your first shift". Computed from the
  // schedulable list we already hold rather than refetching it.
  const setupStatus = useMemo(
    () =>
      computeEventSetupStatus(schedule, {
        schedulableCount: schedulableIdentities.length,
      }),
    [schedule, schedulableIdentities.length]
  )

  // Abort controller for cancelling in-flight schedule requests
  const scheduleAbortRef = useRef<AbortController | null>(null)

  // Whether the currently loaded `schedule` covers the whole event (no date
  // filter), so week switches can skip refetching entirely
  const loadedAllRef = useRef(false)

  // Reload schedule - automatically uses date filtering based on current view mode
  const reloadSchedule = async (weekOverride?: Week | null) => {
    if (!eventId) return

    // Cancel any in-flight request
    scheduleAbortRef.current?.abort()
    const controller = new AbortController()
    scheduleAbortRef.current = controller

    setWeekLoading(true)
    try {
      let fromDate: string | undefined
      let toDate: string | undefined

      if (weekOverride === null) {
        // Explicit null = load all (list view)
      } else if (weekOverride) {
        fromDate = formatDateForApi(weekOverride.firstDay)
        toDate = formatDateForApi(weekOverride.lastDay)
      } else if (viewMode === 'list' || loadAllWeeks) {
        // List view or preload-all: load the whole event
      } else if (weekHelper) {
        // Calendar view: load current week only
        const week = weekHelper.getWeekByRelativeWeekNum(currentWeek)
        fromDate = formatDateForApi(week.firstDay)
        toDate = formatDateForApi(week.lastDay)
      }

      const scheduleData = await ScheduleEndpoints.getEventSchedule(
        parseInt(eventId),
        true,
        fromDate,
        toDate,
        controller.signal
      )

      scheduleData.startDate = parseLocalDate(scheduleData.startDate)
      scheduleData.endDate = parseLocalDate(scheduleData.endDate)
      setSchedule(scheduleData)
      loadedAllRef.current = fromDate === undefined && toDate === undefined
      setWeekLoading(false)
      setSwitchingView(false)
    } catch (error: any) {
      if (error?.name === 'AbortError') return // Request was cancelled, ignore
      console.error('Failed to reload schedule:', error)
      setWeekLoading(false)
      setSwitchingView(false)
    }
  }

  // Initial load
  useEffect(() => {
    if (!eventId) return
    const load = async () => {
      setLoading(true)
      try {
        // Load event (without schedule) + identities in parallel to get dates quickly
        const [eventData, identityData] = await Promise.all([
          EventEndpoints.getEvent(parseInt(eventId)),
          ScheduleEndpoints.getSchedulableForEvent(parseInt(eventId)),
        ])
        eventData.startDate = parseLocalDate(eventData.startDate)
        eventData.endDate = parseLocalDate(eventData.endDate)
        setEvent(eventData)
        setSchedulableIdentities(identityData)

        // Determine current week
        const helper = new EventWeekHelper(eventData)
        const weekNum = helper.currentWeekNum
        setCurrentWeek(weekNum)

        // Load just the current week, or the whole event when preloading
        const week = helper.getWeekByRelativeWeekNum(weekNum)
        const scheduleData = await ScheduleEndpoints.getEventSchedule(
          parseInt(eventId),
          true,
          loadAllWeeks ? undefined : formatDateForApi(week.firstDay),
          loadAllWeeks ? undefined : formatDateForApi(week.lastDay)
        )
        scheduleData.startDate = parseLocalDate(scheduleData.startDate)
        scheduleData.endDate = parseLocalDate(scheduleData.endDate)
        setSchedule(scheduleData)
        loadedAllRef.current = loadAllWeeks
      } catch (error) {
        console.error('Failed to load schedule:', error)
        toast.error(extractErrorMessage(error, 'Failed to load schedule'))
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [eventId])

  // Reload when week changes (calendar view), view mode changes, or the
  // preload-all-weeks toggle changes
  useEffect(() => {
    if (!schedule || !weekHelper || loading) return
    if (viewMode === 'calendar' && !loadAllWeeks) {
      const week = weekHelper.getWeekByRelativeWeekNum(currentWeek)
      reloadSchedule(week)
    } else if (!loadedAllRef.current) {
      // List view or preload-all needs the full event
      reloadSchedule(null)
    } else {
      // Full event already loaded — week/view switches need no refetch
      setSwitchingView(false)
    }
  }, [currentWeek, viewMode, loadAllWeeks])

  const weekHelper = useMemo(() => {
    if (!schedule) return null
    const normalized = {
      ...schedule,
      startDate: parseLocalDate(schedule.startDate),
      endDate: parseLocalDate(schedule.endDate),
    }
    return new EventWeekHelper(normalized)
  }, [schedule])

  // weekHelper.weeks returns a fresh array on every access — memoize so the
  // memos depending on it don't recompute on every render
  const weeks = useMemo(() => weekHelper?.weeks ?? [], [weekHelper])
  const currentWeekData =
    weekHelper && currentWeek < weeks.length ? weeks[currentWeek] : null
  const daysInWeek =
    weekHelper && currentWeek < weeks.length
      ? weekHelper.getDaysInWeek(currentWeek)
      : []

  // Calendar view works off a week-scoped copy of the schedule. When only the
  // week was fetched this is a no-op, but with the whole event preloaded it
  // mirrors the server's fromDate/toDate filter (drop out-of-week recurrences,
  // then shifts left with none) so stats, filters and the grid look the same
  // either way.
  const displaySchedule = useMemo(() => {
    if (!schedule || viewMode !== 'calendar' || !currentWeekData)
      return schedule
    const first = DateTime.fromJSDate(currentWeekData.firstDay).toFormat(
      'yyyy-MM-dd'
    )
    const last = DateTime.fromJSDate(currentWeekData.lastDay).toFormat(
      'yyyy-MM-dd'
    )
    const shifts = schedule.shifts
      .map((s) => ({
        ...s,
        recurrences: s.recurrences.filter((r) => {
          const d = r.date.slice(0, 10)
          return d >= first && d <= last
        }),
      }))
      .filter((s) => s.recurrences.length > 0)
    return { ...schedule, shifts }
  }, [schedule, viewMode, currentWeekData])

  const locations = useMemo(() => {
    if (!displaySchedule) return []
    const locs = new Map<number, Location>()
    displaySchedule.shifts.forEach((s) => {
      if (s.location) locs.set(s.location.id, s.location)
    })
    return [...locs.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [displaySchedule])

  const positions = useMemo(() => {
    if (!displaySchedule) return []
    const pos = new Map<number, Position>()
    displaySchedule.shifts.forEach((s) => {
      if (s.position) pos.set(s.position.id, s.position)
    })
    return [...pos.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [displaySchedule])

  const startTimes = useMemo(() => {
    if (!displaySchedule) return []
    const times = new Set<string>()
    displaySchedule.shifts.forEach((s) => times.add(s.beginTime))
    return [...times].sort()
  }, [displaySchedule])

  const filterableIdentities = useMemo(() => {
    return [...schedulableIdentities].sort(
      (a, b) =>
        a.firstName.localeCompare(b.firstName) ||
        a.lastName.localeCompare(b.lastName)
    )
  }, [schedulableIdentities])

  // Summed in a single pass over the shifts (instead of once per identity) —
  // consumers treat a missing id as 0
  const hoursByIdentityForWeek = useMemo<{ [id: number]: number }>(() => {
    const result: { [id: number]: number } = {}
    if (!schedule || !currentWeekData) return result
    const first = DateTime.fromJSDate(currentWeekData.firstDay).toFormat(
      'yyyy-MM-dd'
    )
    const last = DateTime.fromJSDate(currentWeekData.lastDay).toFormat(
      'yyyy-MM-dd'
    )

    schedule.shifts.forEach((shift) => {
      const shiftHours = shiftDurationHours(shift.beginTime, shift.endTime)
      shift.recurrences.forEach((r) => {
        const d = r.date.slice(0, 10)
        if (d < first || d > last) return
        // Count each identity once per recurrence, matching the previous
        // `spots.some(...)` semantics
        const counted = new Set<number>()
        r.spots.forEach((sp) => {
          if (sp.fulfilledBy && !counted.has(sp.fulfilledBy.id)) {
            counted.add(sp.fulfilledBy.id)
            result[sp.fulfilledBy.id] =
              (result[sp.fulfilledBy.id] || 0) + shiftHours
          }
        })
      })
    })
    return result
  }, [schedule, currentWeekData])

  // Selected position ids, or null when "All Positions" is picked
  const positionFilterIds = useMemo(
    () =>
      positionFilters.length === 0 || positionFilters.includes('all')
        ? null
        : new Set(positionFilters.map((v) => parseInt(v))),
    [positionFilters]
  )

  // Filter shifts
  const filteredShifts = useMemo(() => {
    if (!displaySchedule || switchingView) return []

    const weekDates =
      onlyShiftsWithSpots && weekHelper && currentWeek < weeks.length
        ? new Set(
            weekHelper
              .getDaysInWeek(currentWeek)
              .map((d) => DateTime.fromJSDate(d).toFormat('yyyy-MM-dd'))
          )
        : null

    return displaySchedule.shifts
      .filter(
        (s) =>
          locationFilter === 'all' ||
          s.location?.id === parseInt(locationFilter)
      )
      .filter(
        (s) =>
          !positionFilterIds ||
          (s.position != null && positionFilterIds.has(s.position.id))
      )
      .filter((s) => startTimeFilter === 'all' || s.beginTime === startTimeFilter)
      .filter((s) => {
        if (spotStatusFilter === 'all') return true
        return s.recurrences.some((r) =>
          r.spots.some((sp) => matchesSpotStatus(sp, spotStatusFilter))
        )
      })
      .filter((s) => {
        if (identityFilter === 'all') return true
        const targetId = parseInt(identityFilter)
        return s.recurrences.some((r) =>
          r.spots.some((sp) => sp.fulfilledBy?.id === targetId)
        )
      })
      .filter((s) => {
        if (!weekDates) return true
        return s.recurrences.some(
          (r) => weekDates.has(r.date.slice(0, 10)) && r.spots.length > 0
        )
      })
      .sort(
        (a, b) =>
          a.beginTime.localeCompare(b.beginTime) ||
          a.endTime.localeCompare(b.endTime) ||
          (a.location?.name || '').localeCompare(b.location?.name || '') ||
          (a.position?.name || '').localeCompare(b.position?.name || '') ||
          a.id - b.id
      )
  }, [
    displaySchedule,
    locationFilter,
    positionFilterIds,
    startTimeFilter,
    spotStatusFilter,
    identityFilter,
    onlyShiftsWithSpots,
    weekHelper,
    currentWeek,
    weeks,
    switchingView,
  ])

  // Collapse the calendar back to the first page of shifts when the week or a
  // filter changes — but not on in-place spot updates, so assigning someone
  // deep in an expanded list doesn't snap the view back
  useEffect(() => {
    setVisibleShiftCount(CALENDAR_SHIFTS_PAGE_SIZE)
  }, [
    currentWeek,
    viewMode,
    locationFilter,
    positionFilters,
    startTimeFilter,
    spotStatusFilter,
    identityFilter,
    onlyShiftsWithSpots,
  ])

  const visibleShifts = useMemo(
    () => filteredShifts.slice(0, visibleShiftCount),
    [filteredShifts, visibleShiftCount]
  )
  const hiddenShiftCount = filteredShifts.length - visibleShifts.length

  // Recurrence lookup for calendar cells, keyed by shift id + local ISO date,
  // so each cell is a Map hit instead of a date-parsing scan
  const recurrenceByShiftAndDay = useMemo(() => {
    const map = new Map<string, ShiftRecurrence>()
    displaySchedule?.shifts.forEach((s) =>
      s.recurrences.forEach((r) => map.set(`${s.id}|${r.date.slice(0, 10)}`, r))
    )
    return map
  }, [displaySchedule])

  // Get recurrence for a shift on a specific day
  const getRecurrence = (
    shift: Shift,
    day: Date
  ): ShiftRecurrence | undefined => {
    return recurrenceByShiftAndDay.get(
      `${shift.id}|${DateTime.fromJSDate(day).toFormat('yyyy-MM-dd')}`
    )
  }

  // Stats
  const totalSpots = useMemo(() => {
    if (!displaySchedule) return { total: 0, assigned: 0, unassigned: 0 }
    let total = 0,
      assigned = 0
    displaySchedule.shifts.forEach((s) =>
      s.recurrences.forEach((r) =>
        r.spots.forEach((sp) => {
          total++
          if (sp.state === ShiftSpotState.ASSIGNED) assigned++
        })
      )
    )
    return { total, assigned, unassigned: total - assigned }
  }, [displaySchedule])

  // ─── Handlers ────────────────────────────────────────────────────────

  // Splice a mutation's returned recurrence back into the loaded schedule so
  // single-spot actions don't refetch the whole event. Returns false when the
  // recurrence isn't in the loaded data — callers fall back to reloadSchedule().
  const applyRecurrence = (recurrence: ShiftRecurrence): boolean => {
    if (
      !schedule ||
      !schedule.shifts.some((s) =>
        s.recurrences.some((r) => r.id === recurrence.id)
      )
    ) {
      return false
    }
    setSchedule((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        shifts: prev.shifts.map((s) =>
          s.recurrences.some((r) => r.id === recurrence.id)
            ? {
                ...s,
                recurrences: s.recurrences.map((r) =>
                  r.id === recurrence.id ? recurrence : r
                ),
              }
            : s
        ),
      }
    })
    return true
  }

  // Same idea for endpoints that return a single updated spot
  const applySpot = (spot: ShiftSpot): boolean => {
    if (
      !schedule ||
      !schedule.shifts.some((s) =>
        s.recurrences.some((r) => r.spots.some((sp) => sp.id === spot.id))
      )
    ) {
      return false
    }
    setSchedule((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        shifts: prev.shifts.map((s) => ({
          ...s,
          recurrences: s.recurrences.map((r) =>
            r.spots.some((sp) => sp.id === spot.id)
              ? {
                  ...r,
                  spots: r.spots.map((sp) => (sp.id === spot.id ? spot : sp)),
                }
              : r
          ),
        })),
      }
    })
    return true
  }

  const handleAssignIdentity = async (identityId: number) => {
    if (!assignTarget || !eventId) return
    try {
      if (bulkAssignSpotIds.length > 0) {
        // Bulk assign to multiple spots — endpoint returns nothing, so refetch
        await ScheduleEndpoints.assignIdentityToMultipleSpots({
          shiftSpotIds: bulkAssignSpotIds,
          identityId,
        })
        toast.success(`Assigned to ${bulkAssignSpotIds.length} spot(s)`)
        setBulkAssignSpotIds([])
        await reloadSchedule()
      } else {
        // Single spot assign
        const recurrence = await ScheduleEndpoints.assignIdentityToSpot(
          assignTarget.spot.id,
          { identityId }
        )
        toast.success('Identity assigned')
        if (!applyRecurrence(recurrence)) await reloadSchedule()
      }
      setAssignDialogOpen(false)
      setAssignTarget(null)
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to assign identity'))
    }
  }

  const handleUnassign = async (spotId: number) => {
    if (!eventId) return
    try {
      const recurrence = await ScheduleEndpoints.unassignSpot(spotId)
      if (!applyRecurrence(recurrence)) await reloadSchedule()
      toast.success('Spot unassigned')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to unassign spot'))
    }
  }

  // Drag-and-drop: drop identity onto an unassigned spot
  const handleDropOnSpot = async (spotId: number, e: React.DragEvent) => {
    e.preventDefault()
    if (!eventId) return
    try {
      const data = e.dataTransfer.getData('application/json')
      if (!data) return
      const identity = JSON.parse(data) as QuickProvisioningIdentity

      const recId = spotToRecurrence.get(spotId)
      const date = recId != null ? recurrenceDateById.get(recId) : undefined
      const alreadyHasShiftToday = !!(
        date && assignedDaysByIdentity[identity.id]?.has(date)
      )

      const recurrence = await ScheduleEndpoints.assignIdentityToSpot(spotId, {
        identityId: identity.id,
      })
      if (!applyRecurrence(recurrence)) await reloadSchedule()

      const name = `${identity.firstName} ${identity.lastName}`
      if (alreadyHasShiftToday && date) {
        const dayLabel = DateTime.fromISO(date).toFormat('MMM d')
        toast.warning(
          `Assigned ${name} — already has another shift on ${dayLabel}`
        )
      } else {
        toast.success(`Assigned ${name}`)
      }
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to assign identity'))
    }
  }

  const handleDragOver = (spotState: ShiftSpotState, e: React.DragEvent) => {
    if (spotState === ShiftSpotState.UNASSIGNED) {
      e.preventDefault() // Allow drop only on unassigned spots
    }
  }

  // Build a map: spotId -> recurrenceId, so we enforce one spot per shift-day
  const spotToRecurrence = useMemo(() => {
    const map = new Map<number, number>()
    if (!schedule) return map
    schedule.shifts.forEach((s) =>
      s.recurrences.forEach((r) =>
        r.spots.forEach((sp) => map.set(sp.id, r.id))
      )
    )
    return map
  }, [schedule])

  // recurrenceId -> ISO date
  const recurrenceDateById = useMemo(() => {
    const map = new Map<number, string>()
    if (!schedule) return map
    schedule.shifts.forEach((s) =>
      s.recurrences.forEach((r) => map.set(r.id, r.date))
    )
    return map
  }, [schedule])

  // spotId -> the identity the spot is assigned to (assigned spots only)
  const spotAssignee = useMemo(() => {
    const map = new Map<number, SimpleIdentity>()
    if (!schedule) return map
    schedule.shifts.forEach((s) =>
      s.recurrences.forEach((r) =>
        r.spots.forEach((sp) => {
          if (sp.fulfilledBy) map.set(sp.id, sp.fulfilledBy)
        })
      )
    )
    return map
  }, [schedule])

  // When every selected spot belongs to the same person, the schedule report is
  // for that one employee — the backend only renders the name + POS PIN header
  // when it gets both `employee` and `identityId`.
  const getSpotEmployee = useCallback(
    (spotIds: number[]): { employee: string; identityId: number | null } => {
      const assignees = spotIds
        .map((id) => spotAssignee.get(id))
        .filter((i): i is SimpleIdentity => !!i)
      const unique = new Set(assignees.map((i) => i.id))
      if (unique.size !== 1 || assignees.length !== spotIds.length) {
        return { employee: 'All', identityId: null }
      }
      const identity = assignees[0]
      return {
        employee: `${identity.firstName} ${identity.lastName}`.trim(),
        identityId: identity.id,
      }
    },
    [spotAssignee]
  )

  // Compute the date range (min/max ISO date) covered by a set of selected spots.
  // ISO 'yyyy-MM-dd' strings sort lexicographically, so string comparison works.
  // Falls back to today when no dates are resolvable.
  const getSpotDateRange = useCallback(
    (spotIds: number[]): { fromDate: string; toDate: string } => {
      let min: string | undefined
      let max: string | undefined
      spotIds.forEach((id) => {
        const recId = spotToRecurrence.get(id)
        const d = recId != null ? recurrenceDateById.get(recId) : undefined
        if (!d) return
        if (min === undefined || d < min) min = d
        if (max === undefined || d > max) max = d
      })
      const today = DateTime.now().toFormat('yyyy-MM-dd')
      return { fromDate: min ?? today, toDate: max ?? today }
    },
    [spotToRecurrence, recurrenceDateById]
  )

  // Single place the schedule report request is built. Callers pass the spots
  // and the date range / employee are derived from them, so a request covering
  // one person's spots always carries the identity the PIN header needs.
  const generateScheduleReport = useCallback(
    async (
      spotIds: number[],
      includeTimeFields: boolean,
      includeBreakFields: boolean
    ): Promise<{ content: string; signature: string }> => {
      const { fromDate, toDate } = getSpotDateRange(spotIds)
      const { employee, identityId } = getSpotEmployee(spotIds)
      const res = await ReportEndpoints.getScheduleReport({
        eventId: parseInt(eventId!),
        spotIds,
        location:
          locationFilter === 'all'
            ? 'All'
            : locations.find((l) => l.id === parseInt(locationFilter))?.name ||
              'All',
        position: !positionFilterIds
          ? 'All'
          : positions
              .filter((p) => positionFilterIds.has(p.id))
              .map((p) => p.name)
              .join(', ') || 'All',
        fromDate,
        toDate,
        employee,
        identityId,
        spotStatus: 'All',
        rootUrl: location.origin + '/ems/',
        includeTimeFields,
        includeBreakFields,
      })
      return { content: res.data, signature: res.signature }
    },
    [
      eventId,
      getSpotDateRange,
      getSpotEmployee,
      locationFilter,
      locations,
      positionFilterIds,
      positions,
    ]
  )

  // identityId -> Set of ISO dates already assigned (from the loaded schedule)
  const assignedDaysByIdentity = useMemo(() => {
    const map: { [identityId: number]: Set<string> } = {}
    if (!schedule) return map
    schedule.shifts.forEach((shift) =>
      shift.recurrences.forEach((r) =>
        r.spots.forEach((sp) => {
          if (sp.fulfilledBy) {
            if (!map[sp.fulfilledBy.id]) map[sp.fulfilledBy.id] = new Set()
            map[sp.fulfilledBy.id].add(r.date)
          }
        })
      )
    )
    return map
  }, [schedule])

  // Day options for the sidebar "not working on" filter
  const sidebarDayOptions = useMemo(
    () =>
      (weekHelper && currentWeek < weeks.length
        ? weekHelper.getDaysInWeek(currentWeek)
        : []
      ).map((d) => {
        const dt = DateTime.fromJSDate(d)
        return {
          value: dt.toFormat('yyyy-MM-dd'),
          label: dt.toFormat('ccc, MMM d'),
        }
      }),
    [weekHelper, weeks, currentWeek]
  )

  // Drop the day filter when navigating to a week that doesn't contain it
  useEffect(() => {
    if (
      sidebarFreeOnDate !== 'any' &&
      !sidebarDayOptions.some((o) => o.value === sidebarFreeOnDate)
    )
      setSidebarFreeOnDate('any')
  }, [sidebarDayOptions, sidebarFreeOnDate])

  // yyyy-MM-dd dates the assign dialog is targeting (several for a bulk assign)
  const assignTargetDates = useMemo(() => {
    const dates = new Set<string>()
    if (!assignTarget) return dates
    if (bulkAssignSpotIds.length > 0) {
      bulkAssignSpotIds.forEach((id) => {
        const recId = spotToRecurrence.get(id)
        const d = recId != null ? recurrenceDateById.get(recId) : undefined
        if (d) dates.add(d.slice(0, 10))
      })
    }
    if (dates.size === 0) dates.add(assignTarget.recurrence.date.slice(0, 10))
    return dates
  }, [assignTarget, bulkAssignSpotIds, spotToRecurrence, recurrenceDateById])

  // Identities already working on any of the assign dialog's target dates
  const busyOnAssignDates = useMemo(() => {
    const busy = new Set<number>()
    if (assignTargetDates.size === 0) return busy
    Object.entries(assignedDaysByIdentity).forEach(([id, days]) => {
      for (const d of days) {
        if (assignTargetDates.has(d.slice(0, 10))) {
          busy.add(Number(id))
          break
        }
      }
    })
    return busy
  }, [assignedDaysByIdentity, assignTargetDates])

  // Identities with at least one assigned spot on the selected sidebar day
  const busyOnSidebarDate = useMemo(() => {
    const busy = new Set<number>()
    if (sidebarFreeOnDate === 'any') return busy
    Object.entries(assignedDaysByIdentity).forEach(([id, days]) => {
      for (const d of days) {
        if (d.slice(0, 10) === sidebarFreeOnDate) {
          busy.add(Number(id))
          break
        }
      }
    })
    return busy
  }, [assignedDaysByIdentity, sidebarFreeOnDate])

  // True when the open spot's date already has another shift for the
  // currently selected identity (either an existing assignment or another
  // pending selection in multi-assign mode).
  const spotHasSameDayConflict = (
    spotRecurrenceDate: string,
    spotId: number
  ) => {
    if (!selectedIdentity) return false
    const existing = assignedDaysByIdentity[selectedIdentity.id]
    if (existing && existing.has(spotRecurrenceDate)) return true
    for (const id of selectedSpotIds) {
      if (id === spotId) continue
      const recId = spotToRecurrence.get(id)
      const d = recId != null ? recurrenceDateById.get(recId) : undefined
      if (d === spotRecurrenceDate) return true
    }
    return false
  }

  // Hours per spot, used for the multi-assign hours summary
  const hoursPerSpot = useMemo<{ [spotId: number]: number }>(() => {
    const map: { [spotId: number]: number } = {}
    if (!schedule) return map
    schedule.shifts.forEach((shift) => {
      const hours = shiftDurationHours(shift.beginTime, shift.endTime)
      shift.recurrences.forEach((r) => {
        r.spots.forEach((sp) => {
          map[sp.id] = hours
        })
      })
    })
    return map
  }, [schedule])

  // Multi-assign: toggle a spot selection (one per recurrence)
  const toggleSpotSelection = (spotId: number) => {
    setSelectedSpotIds((prev) => {
      const next = new Set(prev)
      if (next.has(spotId)) {
        next.delete(spotId)
      } else {
        // Remove any other spot from the same recurrence
        const recId = spotToRecurrence.get(spotId)
        if (recId != null) {
          for (const existingId of next) {
            if (spotToRecurrence.get(existingId) === recId) {
              next.delete(existingId)
            }
          }
        }
        next.add(spotId)
      }
      return next
    })
  }

  // Multi-assign: confirm bulk assignment
  const handleBulkAssign = async () => {
    if (!selectedIdentity || selectedSpotIds.size === 0 || !eventId) return
    setBulkAssigning(true)
    try {
      await ScheduleEndpoints.assignIdentityToMultipleSpots({
        identityId: selectedIdentity.id,
        shiftSpotIds: [...selectedSpotIds],
      })
      await reloadSchedule()
      toast.success(
        `Assigned ${selectedIdentity.firstName} ${selectedIdentity.lastName} to ${selectedSpotIds.size} spot${selectedSpotIds.size !== 1 ? 's' : ''}`
      )
      setSelectedIdentity(null)
      setSelectedSpotIds(new Set())
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to bulk assign'))
    } finally {
      setBulkAssigning(false)
    }
  }

  // Cancel multi-assign mode
  const cancelMultiAssign = () => {
    setSelectedIdentity(null)
    setSelectedSpotIds(new Set())
  }

  // Open spot detail dialog
  const openSpotDialog = async (
    spot: ShiftSpot,
    shift: Shift,
    recurrence: ShiftRecurrence
  ) => {
    if (!eventId) return
    try {
      // Reload spot to get fresh punch clock data
      const freshSpot = await ScheduleEndpoints.getShiftSpot(spot.id)
      setSpotDialogData({ spot: freshSpot, shift, recurrence })
      setSpotDialogOpen(true)
    } catch {
      // Fallback to cached data
      setSpotDialogData({ spot, shift, recurrence })
      setSpotDialogOpen(true)
    }
  }

  const handleSaveSpotDetails = async (
    spotId: number,
    request: { notes: string; punchClock: any }
  ) => {
    if (!eventId) return
    try {
      const spot = await ScheduleEndpoints.editShiftSpotDetails(spotId, request)
      if (!applySpot(spot)) await reloadSchedule()
      setSpotDialogOpen(false)
      setSpotDialogData(null)
      toast.success('Spot details saved')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to save spot details'))
    }
  }

  // Mark spot as unassignable (identityId: 0) or back to unassigned (unassign)
  const handleMarkUnassignable = async (spotId: number) => {
    if (!eventId) return
    try {
      const recurrence = await ScheduleEndpoints.assignIdentityToSpot(spotId, {
        identityId: 0,
      })
      if (!applyRecurrence(recurrence)) await reloadSchedule()
      toast.success('Spot marked as unavailable')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to update spot'))
    }
  }

  const handleMarkAvailable = async (spotId: number) => {
    if (!eventId) return
    try {
      const recurrence = await ScheduleEndpoints.unassignSpot(spotId)
      if (!applyRecurrence(recurrence)) await reloadSchedule()
      toast.success('Spot marked as available')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to update spot'))
    }
  }

  // Deleting a shift removes every recurrence and every spot on it, assigned
  // ones included — the server does not refuse assigned spots the way editing
  // a shift does. Confirm with a typed count before letting it through.
  const [deleteShiftTarget, setDeleteShiftTarget] = useState<Shift | null>(null)
  const [deleteShiftPartial, setDeleteShiftPartial] = useState(false)
  const [deleteShiftConfirmText, setDeleteShiftConfirmText] = useState('')
  const [deleteShiftLoading, setDeleteShiftLoading] = useState(false)
  const [deleteShiftFetchingId, setDeleteShiftFetchingId] = useState<number | null>(null)

  const deleteShiftSpots =
    deleteShiftTarget?.recurrences.flatMap((r) => r.spots) ?? []
  const deleteShiftSpotCount = deleteShiftSpots.length
  const deleteShiftAssignedCount = deleteShiftSpots.filter(
    (s) => s.state === ShiftSpotState.ASSIGNED
  ).length
  const deleteShiftDates = (deleteShiftTarget?.recurrences ?? [])
    .map((r) => r.date)
    .sort()
  const deleteShiftConfirmed =
    deleteShiftConfirmText.trim() === String(deleteShiftSpotCount)

  const closeDeleteShift = () => {
    setDeleteShiftTarget(null)
    setDeleteShiftPartial(false)
    setDeleteShiftConfirmText('')
  }

  // In calendar view the cached shift only carries the current week's
  // recurrences, so the counts shown would understate what is about to be
  // deleted. Fetch the full shift first, same as openEditShift.
  const openDeleteShift = async (shift: Shift) => {
    setDeleteShiftFetchingId(shift.id)
    setDeleteShiftConfirmText('')
    try {
      const fullShift = await ScheduleEndpoints.getShift(shift.id)
      setDeleteShiftPartial(false)
      setDeleteShiftTarget(fullShift)
    } catch (error: any) {
      // Fall back to the cached shift, but flag that the counts may cover only
      // the visible week so the dialog doesn't understate the damage silently.
      toast.error(extractErrorMessage(error, 'Failed to load all shift dates'))
      setDeleteShiftPartial(true)
      setDeleteShiftTarget(shift)
    } finally {
      setDeleteShiftFetchingId(null)
    }
  }

  const handleDeleteShift = async () => {
    if (!eventId || !deleteShiftTarget || !deleteShiftConfirmed) return
    setDeleteShiftLoading(true)
    try {
      const updated = await ScheduleEndpoints.deleteShift(deleteShiftTarget.id)
      setSchedule(updated)
      closeDeleteShift()
      toast.success(
        `Shift deleted (${deleteShiftSpotCount} spot${deleteShiftSpotCount === 1 ? '' : 's'})`
      )
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to delete shift'))
    } finally {
      setDeleteShiftLoading(false)
    }
  }

  // Open the edit dialog for a shift. In calendar view the loaded schedule is
  // filtered to the current week, so the cached shift only carries that week's
  // recurrences. Editing from there would drop every other week's dates on save.
  // Fetch the full shift (all recurrences) before opening so the date picker
  // reflects every week the shift runs.
  const openEditShift = async (shift: Shift) => {
    try {
      const fullShift = await ScheduleEndpoints.getShift(shift.id)
      setEditingShift(fullShift)
    } catch (error: any) {
      // Fall back to the cached (week-filtered) shift so editing still works,
      // but warn that other weeks' dates may not be shown.
      toast.error(extractErrorMessage(error, 'Failed to load all shift dates'))
      setEditingShift(shift)
    }
    setShiftDialogOpen(true)
  }

  const handleSaveShift = async (request: ShiftRequest) => {
    if (!eventId) return
    try {
      let updated: EventSchedule
      if (editingShift) {
        updated = await ScheduleEndpoints.editShift(editingShift.id, request)
      } else {
        updated = await ScheduleEndpoints.createShift(
          parseInt(eventId),
          request
        )
      }
      const wasEditing = !!editingShift
      setShiftDialogOpen(false)
      setEditingShift(null)
      // Drop any stale spot selection: the spots it referred to may have just
      // been removed by a spots-to-fill decrease.
      setSelectedSpotIds(new Set())
      if (hasUnidentifiedSpot(updated)) {
        await reloadSchedule()
      } else {
        setSchedule(updated)
      }
      toast.success(wasEditing ? 'Shift updated' : 'Shift created')
    } catch (error: any) {
      toast.error(extractErrorMessage(error, 'Failed to save shift'))
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              Loading schedule...
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!schedule) {
    return (
      <div className="container mx-auto px-4 py-6">
        <p className="text-center text-muted-foreground">Event not found</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/event">Events</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink href={`/event/${eventId}/config`}>
              {schedule.name}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-semibold">Schedule</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">Schedule</CardTitle>
                <CardDescription className="flex items-center gap-3 mt-1">
                  <span>{schedule.name}</span>
                  <Badge variant="secondary">{schedule.client?.name}</Badge>
                  <span className="text-muted-foreground">
                    {totalSpots.assigned}/{totalSpots.total} spots filled
                  </span>
                </CardDescription>
              </div>
            </div>
            <div className="flex gap-2">
              {/* View toggle */}
              <div className="flex border rounded-md">
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'ghost'}
                  size="icon"
                  className="h-9 w-9 rounded-r-none"
                  onClick={() => changeViewMode('calendar')}
                  title="Calendar view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="icon"
                  className="h-9 w-9 rounded-l-none"
                  onClick={() => changeViewMode('list')}
                  title="List view"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
              {!setupStatus.isComplete &&
                schedule.client &&
                hasScheduleAdminFor(schedule.client.id) && (
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate(
                        `${wizardHref(eventId!)}?from=/event/${eventId}/schedule`
                      )
                    }
                    title="Walk through the remaining setup steps"
                  >
                    <Wand2 className="mr-2 h-4 w-4" />
                    Setup Wizard
                  </Button>
                )}
              <Button
                variant="outline"
                onClick={() => navigate(`/event/${eventId}/config`)}
              >
                <Settings className="mr-2 h-4 w-4" />
                Configure
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/event/${eventId}/provision`)}
              >
                <ClipboardList className="mr-2 h-4 w-4" />
                Provision
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!eventId) return
                  try {
                    const info = await EventEndpoints.getBulkBadgeInfo(
                      parseInt(eventId)
                    )
                    if (info.total === 0) {
                      toast.error('No badges to print')
                      return
                    }
                    for (let page = 0; page < info.pages; page++) {
                      BadgePrintingLogic(
                        () =>
                          EventEndpoints.getBulkBadgePdf(
                            parseInt(eventId),
                            page
                          ),
                        false
                      ).catch(() => {})
                    }
                    toast.success(
                      `Generating ${info.total} badges in ${info.pages} PDF${info.pages !== 1 ? 's' : ''} (max 100 per PDF)`
                    )
                  } catch (error) {
                    toast.error(extractErrorMessage(error, 'Failed to generate badges'))
                  }
                }}
              >
                <Printer className="mr-2 h-4 w-4" />
                Print Badges
              </Button>
              {schedule.client && hasScheduleAdminFor(schedule.client.id) && (
                <Button
                  onClick={() => {
                    setEditingShift(null)
                    setShiftDialogOpen(true)
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  New Shift
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Filters bar -- shared between views */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {viewMode === 'calendar' && (
            <>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => setCurrentWeek((w) => Math.max(0, w - 1))}
                disabled={currentWeek === 0}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Select
                value={currentWeek.toString()}
                onValueChange={(v) => setCurrentWeek(parseInt(v))}
              >
                <SelectTrigger className="w-[280px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {weeks.map((week) => (
                    <SelectItem
                      key={week.relativeWeekNum}
                      value={week.relativeWeekNum.toString()}
                    >
                      Week {week.relativeWeekNum + 1}:{' '}
                      {formatDateShort(week.firstDay)} -{' '}
                      {formatDateShort(week.lastDay)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() =>
                  setCurrentWeek((w) => Math.min(weeks.length - 1, w + 1))
                }
                disabled={currentWeek >= weeks.length - 1}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
          {viewMode === 'list' && (
            <p className="text-sm text-muted-foreground">
              Showing all shift spots across the entire event
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Combobox
            className="w-[160px]"
            value={locationFilter}
            onChange={setLocationFilter}
            placeholder="All Locations"
            searchPlaceholder="Search locations…"
            emptyText="No locations found"
            options={[
              { value: 'all', label: 'All Locations' },
              ...locations.map((l) => ({
                value: l.id.toString(),
                label: l.name,
              })),
            ]}
          />
          <MultiCombobox
            className="w-[160px]"
            values={positionFilters}
            onChange={setPositionFilters}
            placeholder="All Positions"
            searchPlaceholder="Search positions…"
            emptyText="No positions found"
            options={[
              { value: 'all', label: 'All Positions' },
              ...positions.map((p) => ({
                value: p.id.toString(),
                label: p.name,
              })),
            ]}
          />
          <Select value={startTimeFilter} onValueChange={setStartTimeFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All Start Times" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Start Times</SelectItem>
              {startTimes.map((t) => (
                <SelectItem key={t} value={t}>
                  {formatStartTime(t)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={spotStatusFilter} onValueChange={setSpotStatusFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SPOT_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Combobox
            className="w-[200px]"
            value={identityFilter}
            onChange={setIdentityFilter}
            placeholder="All Employees"
            searchPlaceholder="Search employees…"
            emptyText="No employees found"
            options={[
              { value: 'all', label: 'All Employees' },
              ...filterableIdentities.map((i) => ({
                value: i.id.toString(),
                label: `${i.firstName} ${i.lastName}`,
              })),
            ]}
          />
          {viewMode === 'calendar' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  View
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuCheckboxItem
                  checked={onlyShiftsWithSpots}
                  onCheckedChange={(c) => setOnlyShiftsWithSpots(c === true)}
                  onSelect={(e) => e.preventDefault()}
                >
                  Only shifts with spots this week
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={loadAllWeeks}
                  onCheckedChange={(c) => setLoadAllWeeks(c === true)}
                  onSelect={(e) => e.preventDefault()}
                >
                  <span className="flex flex-col">
                    Load all weeks up front
                    <span className="text-xs text-muted-foreground">
                      One bigger load, then week switching is instant
                    </span>
                  </span>
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {viewMode === 'calendar' && (
        <div className="relative">
          {(weekLoading || switchingView) && (
            <div className="absolute inset-0 bg-background/60 z-50 flex items-center justify-center rounded-lg">
              <div className="flex items-center gap-2 bg-background border rounded-md px-4 py-2 shadow-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">
                  {loadAllWeeks ? 'Loading schedule...' : 'Loading week...'}
                </span>
              </div>
            </div>
          )}
          {/* Legend */}
          <Card>
            <CardContent className="p-3">
              <div className="space-y-2">
                {/* Spot status legend */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">Spots:</span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-flex items-center justify-center h-4 w-6 rounded bg-slate-100 border border-slate-300" />
                    Assigned (click to unassign)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-flex items-center justify-center h-4 w-6 rounded bg-gray-50 border border-dashed border-gray-300" />
                    Open (click to assign, right-click for N/A)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-flex items-center justify-center h-4 w-6 rounded bg-red-50 border border-red-200 line-through text-[8px] leading-none text-red-400">
                      N/A
                    </span>
                    Unavailable (click to restore)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-primary/10" />
                    Drag from sidebar
                  </span>
                </div>
                {/* Punch clock color legend */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">
                    Punch Clock:
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-full bg-slate-200 border border-slate-400" />
                    Pending
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-full bg-green-200 border border-green-300" />
                    Checked In
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-full bg-purple-200 border border-purple-300" />
                    On Break
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-full bg-blue-200 border border-blue-300" />
                    Checked Out
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-full bg-amber-200 border border-amber-300" />
                    Late / Missed
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-full bg-rose-200 border border-rose-300" />
                    Error
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="font-semibold text-foreground">*</span>
                    Took a break
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Schedule Grid + Employee Sidebar */}
          <div className="flex gap-4">
            <div className="flex-1 min-w-0">
              {filteredShifts.length === 0 ? (
                <Card>
                  <CardContent className="p-0">
                    {!setupStatus.canCreateShifts ? (
                      <div className="p-6">
                        <SetupPrerequisiteAlert
                          eventId={eventId!}
                          missing={setupStatus.missingForShifts}
                          action="create a shift"
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16">
                        <Calendar className="h-10 w-10 text-muted-foreground mb-3" />
                        <p className="text-sm font-medium">No shifts</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Create your first shift to start building the schedule
                        </p>
                        <Button
                          className="mt-4"
                          onClick={() => {
                            setEditingShift(null)
                            setShiftDialogOpen(true)
                          }}
                          disabled={
                            !schedule.client ||
                            !hasScheduleAdminFor(schedule.client.id)
                          }
                          title={
                            schedule.client &&
                            !hasScheduleAdminFor(schedule.client.id)
                              ? 'You do not have permission to create shifts for this event'
                              : undefined
                          }
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          New Shift
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <>
                <table className="w-full table-fixed caption-bottom text-sm border rounded-lg">
                  <TableHeader className="sticky top-14 z-20 shadow-[0_1px_3px_0_rgb(0,0,0,0.1)] [&_th]:bg-background">
                    <TableRow className="hover:bg-transparent border-b">
                      <TableHead className="sticky left-0 z-30 bg-background w-[200px]">
                        Shift
                      </TableHead>
                      {daysInWeek.map((day) => {
                        const isInEvent = weekHelper?.isDayInEvent(day)
                        return (
                          <TableHead
                            key={day.toISOString()}
                            className={cn(
                              'text-center',
                              !isInEvent && 'opacity-40'
                            )}
                          >
                            <div className="text-xs">
                              {DateTime.fromJSDate(day).toFormat('EEE')}
                            </div>
                            <div className="font-semibold">
                              {DateTime.fromJSDate(day).toFormat('MMM d')}
                            </div>
                          </TableHead>
                        )
                      })}
                      <TableHead className="w-[40px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleShifts.map((shift) => (
                      <TableRow key={shift.id}>
                        <TableCell className="sticky left-0 bg-background z-10">
                          <div className="space-y-0.5">
                            <p className="font-medium text-sm">
                              {formatTime(shift.beginTime)} -{' '}
                              {formatTime(shift.endTime)}
                            </p>
                            <div className="flex items-start gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3 shrink-0 mt-0.5" />
                              <span className="min-w-0 break-words">
                                {shift.location?.name || '-'}
                              </span>
                            </div>
                            <div className="flex items-start gap-1 text-xs text-muted-foreground">
                              <Briefcase className="h-3 w-3 shrink-0 mt-0.5" />
                              <span className="min-w-0 break-words">
                                {shift.position?.name || '-'}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {shift.spotsToFill} spot
                              {shift.spotsToFill !== 1 ? 's' : ''}
                              {shift.notes && ` - ${shift.notes}`}
                            </p>
                          </div>
                        </TableCell>
                        {daysInWeek.map((day) => {
                          const recurrence = getRecurrence(shift, day)
                          const isInEvent = weekHelper?.isDayInEvent(day)
                          if (!recurrence) {
                            return (
                              <TableCell
                                key={day.toISOString()}
                                className={cn(
                                  'text-center',
                                  !isInEvent && 'opacity-40 bg-muted/20'
                                )}
                              >
                                <span className="text-xs text-muted-foreground">
                                  -
                                </span>
                              </TableCell>
                            )
                          }
                          return (
                            <TableCell key={day.toISOString()} className="p-1">
                              <div className="space-y-0.5">
                                {[...recurrence.spots]
                                  .sort((a, b) => a.id - b.id)
                                  .map((spot) => {
                                    const isMultiMode = !!selectedIdentity
                                    const isSelected = selectedSpotIds.has(
                                      spot.id
                                    )
                                    const isOpenSpot =
                                      spot.state === ShiftSpotState.UNASSIGNED

                                    return (
                                      <div
                                        key={spot.id}
                                        className={cn(
                                          'rounded px-1.5 py-0.5 text-xs cursor-pointer transition-colors flex items-center gap-1',
                                          spot.state ===
                                            ShiftSpotState.ASSIGNED &&
                                            punchClockSpotClass(
                                              spot.punchClockState
                                            ),
                                          spot.state ===
                                            ShiftSpotState.UNASSIGNABLE &&
                                            'bg-red-50 text-red-400 line-through hover:bg-red-100',
                                          isOpenSpot &&
                                            !isMultiMode &&
                                            'bg-gray-50 text-gray-400 hover:bg-gray-100 border border-dashed border-gray-300',
                                          isOpenSpot &&
                                            isMultiMode &&
                                            !isSelected &&
                                            'bg-gray-100 text-gray-500 hover:bg-primary/10 border border-dashed border-gray-300',
                                          isOpenSpot &&
                                            isMultiMode &&
                                            isSelected &&
                                            'bg-primary/15 text-primary border border-primary'
                                        )}
                                        onDragOver={(e) =>
                                          !isMultiMode &&
                                          handleDragOver(spot.state, e)
                                        }
                                        onDragEnter={(e) => {
                                          if (!isMultiMode && isOpenSpot) {
                                            e.preventDefault()
                                            e.currentTarget.classList.add(
                                              'border-primary',
                                              'bg-primary/10'
                                            )
                                          }
                                        }}
                                        onDragLeave={(e) => {
                                          if (!isMultiMode) {
                                            e.currentTarget.classList.remove(
                                              'border-primary',
                                              'bg-primary/10'
                                            )
                                          }
                                        }}
                                        onDrop={(e) => {
                                          if (!isMultiMode) {
                                            e.currentTarget.classList.remove(
                                              'border-primary',
                                              'bg-primary/10'
                                            )
                                            handleDropOnSpot(spot.id, e)
                                          }
                                        }}
                                        onContextMenu={(e) => {
                                          e.preventDefault()
                                          if (isOpenSpot) {
                                            handleMarkUnassignable(spot.id)
                                          } else if (
                                            spot.state ===
                                            ShiftSpotState.UNASSIGNABLE
                                          ) {
                                            handleMarkAvailable(spot.id)
                                          }
                                        }}
                                        onClick={() => {
                                          if (isMultiMode && isOpenSpot) {
                                            toggleSpotSelection(spot.id)
                                          } else if (
                                            spot.state ===
                                              ShiftSpotState.ASSIGNED ||
                                            spot.state ===
                                              ShiftSpotState.UNASSIGNABLE
                                          ) {
                                            openSpotDialog(
                                              spot,
                                              shift,
                                              recurrence
                                            )
                                          } else if (isOpenSpot) {
                                            setAssignTarget({
                                              spot,
                                              shift,
                                              recurrence,
                                            })
                                            setAssignSearch('')
                                            setAssignDialogOpen(true)
                                          }
                                        }}
                                      >
                                        {isMultiMode && isOpenSpot && (
                                          <>
                                            <input
                                              type="checkbox"
                                              checked={isSelected}
                                              readOnly
                                              className="h-3 w-3 rounded pointer-events-none"
                                            />
                                            {spotHasSameDayConflict(
                                              recurrence.date,
                                              spot.id
                                            ) && (
                                              <AlertTriangle
                                                className="h-3 w-3 text-amber-500 shrink-0"
                                                aria-label="Already has a shift this day"
                                              />
                                            )}
                                          </>
                                        )}
                                        <span className="truncate">
                                          {spot.fulfilledBy
                                            ? `${spot.fulfilledBy.firstName} ${spot.fulfilledBy.lastName?.charAt(0)}.${spot.hadBreak ? '*' : ''}`
                                            : spot.unassignable
                                              ? 'N/A'
                                              : isMultiMode
                                                ? isSelected
                                                  ? selectedIdentity!.firstName
                                                  : 'Select'
                                                : 'Open'}
                                        </span>
                                      </div>
                                    )
                                  })}
                                <div className="flex justify-center pt-0.5">
                                  <RecurrenceStatusBadge
                                    state={recurrence.state}
                                  />
                                </div>
                              </div>
                            </TableCell>
                          )
                        })}
                        <TableCell>
                          {schedule.client &&
                            hasScheduleAdminFor(schedule.client.id) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    className="h-7 w-7 p-0"
                                  >
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => openEditShift(shift)}
                                  >
                                    <Edit className="h-4 w-4 mr-2" />
                                    Edit Shift
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openDeleteShift(shift)}
                                    disabled={deleteShiftFetchingId === shift.id}
                                    className="text-destructive"
                                  >
                                    {deleteShiftFetchingId === shift.id ? (
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4 mr-2" />
                                    )}
                                    Delete Shift
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </table>
                {hiddenShiftCount > 0 && (
                  <div className="flex items-center justify-center gap-3 border border-t-0 rounded-b-lg bg-muted/30 py-2 text-sm">
                    <span className="text-muted-foreground">
                      Showing {visibleShifts.length} of {filteredShifts.length}{' '}
                      shifts
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setVisibleShiftCount(
                          (c) => c + CALENDAR_SHIFTS_PAGE_SIZE
                        )
                      }
                    >
                      Load{' '}
                      {Math.min(CALENDAR_SHIFTS_PAGE_SIZE, hiddenShiftCount)}{' '}
                      more
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setVisibleShiftCount(filteredShifts.length)
                      }
                    >
                      Show all {filteredShifts.length}
                    </Button>
                  </div>
                )}
                </>
              )}
            </div>

            {/* Employee Sidebar */}
            {sidebarOpen && (
              <Card className="w-[260px] shrink-0 self-start sticky top-20">
                <CardHeader className="p-3 pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm flex items-center gap-1.5">
                      <Users className="h-4 w-4" />
                      Employees
                    </CardTitle>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <div className="relative pt-1">
                    <Search className="absolute left-2.5 top-1/2 translate-y-[-25%] h-3.5 w-3.5 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      value={sidebarSearch}
                      onChange={(e) => setSidebarSearch(e.target.value)}
                      className="h-8 pl-8 text-xs"
                    />
                  </div>
                  <Select
                    value={sidebarFreeOnDate}
                    onValueChange={setSidebarFreeOnDate}
                  >
                    <SelectTrigger className="h-8 text-xs mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any" className="text-xs">
                        All employees
                      </SelectItem>
                      {sidebarDayOptions.map((o) => (
                        <SelectItem
                          key={o.value}
                          value={o.value}
                          className="text-xs"
                        >
                          Not working {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="max-h-[calc(100vh-280px)] overflow-y-auto">
                    {schedulableIdentities.length === 0 && (
                      <div className="space-y-2 px-3 py-6 text-center">
                        <p className="text-xs font-medium">
                          No staff provisioned yet
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {setupStatus.byId.roles.done
                            ? 'Provision people into a schedulable role and they will appear here, ready to drag onto shifts.'
                            : 'This event has no schedulable role yet. Add one first, then provision people into it.'}
                        </p>
                        <Button asChild size="sm" variant="outline">
                          <Link
                            to={setupStepHref(
                              setupStatus.byId.roles.done ? 'staff' : 'roles',
                              eventId!
                            )}
                          >
                            <UserPlus className="mr-2 h-3.5 w-3.5" />
                            {setupStatus.byId.roles.done
                              ? 'Provision staff'
                              : 'Set up roles'}
                          </Link>
                        </Button>
                      </div>
                    )}
                    {schedulableIdentities
                      .filter((i) => !busyOnSidebarDate.has(i.id))
                      .filter((i) => {
                        if (!sidebarSearch.trim()) return true
                        const term = sidebarSearch.toLowerCase()
                        return `${i.firstName} ${i.lastName} ${i.email || ''}`
                          .toLowerCase()
                          .includes(term)
                      })
                      .sort(
                        (a, b) =>
                          a.firstName.localeCompare(b.firstName) ||
                          a.lastName.localeCompare(b.lastName)
                      )
                      .map((identity) => {
                        const isSelected = selectedIdentity?.id === identity.id
                        return (
                          <div
                            key={identity.id}
                            draggable={!selectedIdentity}
                            onDragStart={(e) => {
                              if (selectedIdentity) return
                              e.dataTransfer.setData(
                                'application/json',
                                JSON.stringify(identity)
                              )
                              e.dataTransfer.effectAllowed = 'copy'
                            }}
                            onClick={() => {
                              if (isSelected) {
                                cancelMultiAssign()
                              } else {
                                setSelectedIdentity(identity)
                                setSelectedSpotIds(new Set())
                              }
                            }}
                            className={cn(
                              'flex items-center gap-2 px-3 py-1.5 text-xs border-b border-border/50 transition-colors',
                              isSelected
                                ? 'bg-primary/10 text-primary cursor-pointer'
                                : selectedIdentity
                                  ? 'hover:bg-muted/50 cursor-pointer opacity-50'
                                  : 'hover:bg-muted/50 cursor-grab active:cursor-grabbing'
                            )}
                          >
                            <div
                              className={cn(
                                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                                isSelected
                                  ? 'bg-primary text-primary-foreground'
                                  : 'bg-primary/10 text-primary'
                              )}
                            >
                              {(identity.firstName?.[0] || '').toUpperCase()}
                              {(identity.lastName?.[0] || '').toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium truncate">
                                {identity.firstName} {identity.lastName}
                              </p>
                            </div>
                            {isSelected ? (
                              <Check className="h-3.5 w-3.5 ml-auto shrink-0" />
                            ) : (
                              <span
                                className={cn(
                                  'ml-auto shrink-0 text-[11px] tabular-nums',
                                  event &&
                                    hoursByIdentityForWeek[identity.id] >
                                      event.overtimeThreshold
                                    ? 'text-destructive font-semibold'
                                    : 'text-muted-foreground'
                                )}
                              >
                                {Math.round(
                                  (hoursByIdentityForWeek[identity.id] ?? 0) *
                                    10
                                ) / 10}
                                hrs
                              </span>
                            )}
                          </div>
                        )
                      })}
                  </div>
                  <div className="px-3 py-2 border-t text-xs text-muted-foreground text-center">
                    {sidebarFreeOnDate === 'any'
                      ? `${schedulableIdentities.length} employees`
                      : `${schedulableIdentities.filter((i) => !busyOnSidebarDate.has(i.id)).length} of ${schedulableIdentities.length} free on ${
                          sidebarDayOptions.find(
                            (o) => o.value === sidebarFreeOnDate
                          )?.label ?? ''
                        }`}
                  </div>
                </CardContent>
              </Card>
            )}
            {!sidebarOpen && (
              <Button
                variant="outline"
                size="sm"
                className="self-start sticky top-20"
                onClick={() => setSidebarOpen(true)}
              >
                <Users className="h-4 w-4 mr-1" />
                Employees
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ─── List View ─────────────────────────────────────────────── */}
      {viewMode === 'list' && (!switchingView ? (
        <ScheduleListView
          schedule={schedule}
          locationFilter={locationFilter}
          positionFilterIds={positionFilterIds}
          startTimeFilter={startTimeFilter}
          spotStatusFilter={spotStatusFilter}
          identityFilter={identityFilter}
          onUnassign={handleUnassign}
          onAssignClick={(spot, shift, recurrence) => {
            setAssignTarget({ spot, shift, recurrence })
            setAssignSearch('')
            setAssignDialogOpen(true)
          }}
          onMarkUnassignable={handleMarkUnassignable}
          onMarkAvailable={handleMarkAvailable}
          onSpotClick={(spot, shift, recurrence) =>
            openSpotDialog(spot, shift, recurrence)
          }
          onBulkAssign={(spotIds) => {
            // Open assign dialog with bulk spot IDs — find first unassigned spot to use as target
            const allRows: { spot: ShiftSpot; shift: Shift; recurrence: ShiftRecurrence }[] = []
            schedule.shifts.forEach(shift => {
              shift.recurrences.forEach(rec => {
                rec.spots.forEach(spot => {
                  if (spotIds.includes(spot.id) && spot.state === 'UNASSIGNED') {
                    allRows.push({ spot, shift, recurrence: rec })
                  }
                })
              })
            })
            if (allRows.length > 0) {
              setBulkAssignSpotIds(spotIds)
              setAssignTarget(allRows[0])
              setAssignSearch('')
              setAssignDialogOpen(true)
            }
          }}
          onBulkClockAsScheduled={async (spotIds) => {
            await ScheduleEndpoints.punchForSpotsAsScheduled({ shiftSpotIds: spotIds })
            await reloadSchedule()
          }}
          onPrintEmail={async (spotIds, includeTimeFields, includeBreakFields) => {
            try {
              const res = await generateScheduleReport(
                spotIds,
                includeTimeFields,
                includeBreakFields
              )
              // Open in new window for printing
              const w = window.open()
              if (w) {
                w.document.write(res.content)
                w.document.close()
              }
            } catch (error) {
              toast.error(extractErrorMessage(error, 'Failed to generate schedule report'))
            }
          }}
          onEmail={async (spotIds, includeTimeFields, includeBreakFields) => {
            try {
              return await generateScheduleReport(
                spotIds,
                includeTimeFields,
                includeBreakFields
              )
            } catch (error) {
              toast.error(extractErrorMessage(error, 'Failed to generate schedule report'))
              return null
            }
          }}
          // Used by the per-employee bulk send: throws instead of toasting so
          // the dialog can report failures per recipient.
          onGenerateEmployeeReport={(spotIds) =>
            generateScheduleReport(spotIds, false, false)
          }
        />
      ) : (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">
            Loading shifts...
          </span>
        </div>
      ))}

      {/* Multi-assign floating action bar */}
      {selectedIdentity && (() => {
        // Selection can span weeks (with "Load all weeks" on), so only spots
        // dated within the current week count toward the week total — the rest
        // roll up into a separate running total
        const weekFirst = currentWeekData
          ? DateTime.fromJSDate(currentWeekData.firstDay).toFormat('yyyy-MM-dd')
          : ''
        const weekLast = currentWeekData
          ? DateTime.fromJSDate(currentWeekData.lastDay).toFormat('yyyy-MM-dd')
          : ''
        let hoursToAdd = 0
        let hoursThisWeek = 0
        selectedSpotIds.forEach((id) => {
          const hours = hoursPerSpot[id] ?? 0
          hoursToAdd += hours
          const recId = spotToRecurrence.get(id)
          const d =
            recId != null ? recurrenceDateById.get(recId)?.slice(0, 10) : undefined
          if (d && d >= weekFirst && d <= weekLast) hoursThisWeek += hours
        })
        const hoursOtherWeeks = hoursToAdd - hoursThisWeek
        const currentWeekHours =
          hoursByIdentityForWeek[selectedIdentity.id] ?? 0
        const projectedWeekHours = currentWeekHours + hoursThisWeek
        const wouldOvertime =
          !!event && projectedWeekHours > event.overtimeThreshold
        const fmt = (n: number) => (Math.round(n * 10) / 10).toString()

        const existingDays =
          assignedDaysByIdentity[selectedIdentity.id] ?? new Set<string>()
        const dateCounts = new Map<string, number>()
        selectedSpotIds.forEach((id) => {
          const recId = spotToRecurrence.get(id)
          const d = recId != null ? recurrenceDateById.get(recId) : undefined
          if (!d) return
          dateCounts.set(d, (dateCounts.get(d) ?? 0) + 1)
        })
        const conflictDates: string[] = []
        dateCounts.forEach((count, d) => {
          if (existingDays.has(d) || count > 1) conflictDates.push(d)
        })
        return (
        <div className="fixed bottom-0 left-0 right-0 z-40 border-t bg-background shadow-lg">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                {(selectedIdentity.firstName?.[0] || '').toUpperCase()}
                {(selectedIdentity.lastName?.[0] || '').toUpperCase()}
              </div>
              <div>
                <p className="text-sm font-medium">
                  Assigning: {selectedIdentity.firstName}{' '}
                  {selectedIdentity.lastName}
                </p>
                {selectedSpotIds.size === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Click open spots in the grid to select them
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {selectedSpotIds.size} spot
                    {selectedSpotIds.size !== 1 ? 's' : ''} selected
                    {' · '}
                    <span className="font-medium text-foreground">
                      +{fmt(hoursToAdd)} hrs
                    </span>
                    {' · week total '}
                    {fmt(currentWeekHours)} →{' '}
                    <span
                      className={cn(
                        wouldOvertime && 'text-destructive font-semibold'
                      )}
                    >
                      {fmt(projectedWeekHours)} hrs
                    </span>
                    {hoursOtherWeeks > 0 && (
                      <>
                        {' · '}
                        <span className="font-medium text-foreground">
                          +{fmt(hoursOtherWeeks)} hrs
                        </span>{' '}
                        in other weeks
                      </>
                    )}
                  </p>
                )}
                {conflictDates.length > 0 && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    Already has a shift on{' '}
                    {conflictDates
                      .sort()
                      .map((d) => DateTime.fromISO(d).toFormat('MMM d'))
                      .join(', ')}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={cancelMultiAssign}
                disabled={bulkAssigning}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleBulkAssign}
                disabled={selectedSpotIds.size === 0 || bulkAssigning}
              >
                {bulkAssigning ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Assigning...
                  </>
                ) : (
                  `Assign to ${selectedSpotIds.size} Spot${selectedSpotIds.size !== 1 ? 's' : ''}`
                )}
              </Button>
            </div>
          </div>
        </div>
        )
      })()}

      {/* Shift Spot Detail Dialog */}
      <ShiftSpotDetailDialog
        open={spotDialogOpen}
        onOpenChange={(open) => {
          setSpotDialogOpen(open)
          if (!open) setSpotDialogData(null)
        }}
        data={spotDialogData}
        timezone={schedule.timezone || schedule.client?.timezone || 'UTC'}
        onSave={handleSaveSpotDetails}
        onUnassign={async (spotId) => {
          await handleUnassign(spotId)
          setSpotDialogOpen(false)
          setSpotDialogData(null)
        }}
        onMarkAvailable={async (spotId) => {
          await handleMarkAvailable(spotId)
          setSpotDialogOpen(false)
          setSpotDialogData(null)
        }}
      />

      {/* Shift Create/Edit Dialog */}
      <ShiftFormDialog
        open={shiftDialogOpen}
        onOpenChange={(open) => {
          setShiftDialogOpen(open)
          if (!open) setEditingShift(null)
        }}
        shift={editingShift}
        eventId={eventId!}
        locations={schedule.locations || []}
        positions={schedule.positions || []}
        daysInEvent={
          weekHelper
            ? weeks.flatMap((w) =>
                weekHelper
                  .getDaysInWeek(w.relativeWeekNum)
                  .filter((d) => weekHelper.isDayInEvent(d))
              )
            : []
        }
        onSave={handleSaveShift}
      />

      {/* Delete Shift Confirmation */}
      <Dialog
        open={deleteShiftTarget !== null}
        onOpenChange={(open) => {
          if (deleteShiftLoading || open) return
          closeDeleteShift()
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Delete this shift?
            </DialogTitle>
            <DialogDescription className="text-left">
              {deleteShiftTarget && (
                <>
                  {formatTime(deleteShiftTarget.beginTime)} -{' '}
                  {formatTime(deleteShiftTarget.endTime)}
                  {deleteShiftTarget.location?.name && (
                    <> at {deleteShiftTarget.location.name}</>
                  )}
                  {deleteShiftTarget.position?.name && (
                    <> ({deleteShiftTarget.position.name})</>
                  )}
                  {deleteShiftDates.length > 0 && (
                    <>
                      , {deleteShiftDates.length} date
                      {deleteShiftDates.length === 1 ? '' : 's'} from{' '}
                      {DateTime.fromISO(deleteShiftDates[0]).toFormat('MMM d, yyyy')}
                      {deleteShiftDates[0] !==
                        deleteShiftDates[deleteShiftDates.length - 1] && (
                        <>
                          {' '}to{' '}
                          {DateTime.fromISO(
                            deleteShiftDates[deleteShiftDates.length - 1]
                          ).toFormat('MMM d, yyyy')}
                        </>
                      )}
                    </>
                  )}
                  .
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              This deletes all{' '}
              <span className="font-semibold">{deleteShiftSpotCount}</span> shift
              spot{deleteShiftSpotCount === 1 ? '' : 's'} on every date of this
              shift
              {deleteShiftAssignedCount > 0 && (
                <>
                  , including{' '}
                  <span className="font-semibold">
                    {deleteShiftAssignedCount}
                  </span>{' '}
                  assigned spot
                  {deleteShiftAssignedCount === 1 ? '' : 's'} and any punch clock
                  data on {deleteShiftAssignedCount === 1 ? 'it' : 'them'}
                </>
              )}
              . This cannot be undone.
              {deleteShiftPartial && (
                <>
                  {' '}
                  <span className="font-semibold">
                    The full shift could not be loaded, so these counts may cover
                    only the week you are viewing — the delete still removes every
                    date.
                  </span>
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="delete-shift-confirm-count" className="text-sm">
                Type{' '}
                <span className="font-semibold text-foreground">
                  {deleteShiftSpotCount}
                </span>{' '}
                to confirm
              </Label>
              <Input
                id="delete-shift-confirm-count"
                value={deleteShiftConfirmText}
                onChange={(e) => setDeleteShiftConfirmText(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === 'Enter' &&
                    deleteShiftConfirmed &&
                    !deleteShiftLoading
                  ) {
                    handleDeleteShift()
                  }
                }}
                placeholder={String(deleteShiftSpotCount)}
                autoFocus
                autoComplete="off"
                disabled={deleteShiftLoading}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={closeDeleteShift}
              disabled={deleteShiftLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteShift}
              disabled={!deleteShiftConfirmed || deleteShiftLoading}
              className="flex items-center gap-2"
            >
              {deleteShiftLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {deleteShiftLoading ? 'Deleting...' : 'Delete Shift'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Identity Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Assign Identity</DialogTitle>
            <DialogDescription>
              {assignTarget && (
                <>
                  Assign to {formatTime(assignTarget.shift.beginTime)} -{' '}
                  {formatTime(assignTarget.shift.endTime)} on{' '}
                  {formatDateShort(assignTarget.recurrence.date)}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search employees..."
              value={assignSearch}
              onChange={(e) => setAssignSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="assign-free-only"
              checked={assignFreeOnly}
              onCheckedChange={(v) => setAssignFreeOnly(v === true)}
            />
            <Label
              htmlFor="assign-free-only"
              className="text-sm font-normal cursor-pointer"
            >
              Only show staff not already working{' '}
              {assignTargetDates.size > 1 ? 'these days' : 'this day'}
            </Label>
          </div>
          <div className="max-h-[300px] overflow-y-auto space-y-1">
            {schedulableIdentities.length === 0 && (
              <SetupPrerequisiteAlert
                eventId={eventId!}
                missing={setupStatus.missingForStaffing}
                action="assign someone"
                showWizardLink={false}
              />
            )}
            {schedulableIdentities
              .filter((i) => !assignFreeOnly || !busyOnAssignDates.has(i.id))
              .filter((i) => {
                if (!assignSearch.trim()) return true
                const term = assignSearch.toLowerCase()
                return `${i.firstName} ${i.lastName} ${i.email || ''}`
                  .toLowerCase()
                  .includes(term)
              })
              .sort(
                (a, b) =>
                  a.firstName.localeCompare(b.firstName) ||
                  a.lastName.localeCompare(b.lastName)
              )
              .slice(0, 50)
              .map((identity) => (
                <button
                  key={identity.id}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-left hover:bg-muted transition-colors"
                  onClick={() => handleAssignIdentity(identity.id)}
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                    {(identity.firstName?.[0] || '').toUpperCase()}
                    {(identity.lastName?.[0] || '').toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {identity.firstName} {identity.lastName}
                    </p>
                    {identity.email && (
                      <p className="text-xs text-muted-foreground">
                        {identity.email}
                      </p>
                    )}
                  </div>
                  {busyOnAssignDates.has(identity.id) && (
                    <Badge variant="secondary" className="shrink-0 text-[10px]">
                      Already working
                    </Badge>
                  )}
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Shift Form Dialog ───────────────────────────────────────────────────

interface ShiftFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shift: Shift | null
  eventId: string
  locations: Location[]
  positions: Position[]
  daysInEvent: Date[]
  onSave: (request: ShiftRequest) => Promise<void>
}

const ShiftFormDialog: React.FC<ShiftFormDialogProps> = ({
  open,
  onOpenChange,
  shift,
  eventId,
  locations,
  positions,
  daysInEvent,
  onSave,
}) => {
  const [saving, setSaving] = useState(false)
  const [locationId, setLocationId] = useState('')
  const [positionId, setPositionId] = useState('')
  const [beginTime, setBeginTime] = useState('09:00')
  const [endTime, setEndTime] = useState('17:00')
  const [spotsToFill, setSpotsToFill] = useState(1)
  const [notes, setNotes] = useState('')
  const [selectedDates, setSelectedDates] = useState<Date[]>([])

  // Event date boundaries for the calendar
  const eventStart = daysInEvent.length > 0 ? daysInEvent[0] : undefined
  const eventEnd =
    daysInEvent.length > 0 ? daysInEvent[daysInEvent.length - 1] : undefined

  // Populate form when editing
  useEffect(() => {
    if (open && shift) {
      setLocationId(shift.location?.id?.toString() || '')
      setPositionId(shift.position?.id?.toString() || '')
      setBeginTime(shift.beginTime?.substring(0, 5) || '09:00')
      setEndTime(shift.endTime?.substring(0, 5) || '17:00')
      setSpotsToFill(shift.spotsToFill || 1)
      setNotes(shift.notes || '')
      setSelectedDates(
        shift.recurrences.map((r) => DateTime.fromISO(r.date).toJSDate())
      )
    } else if (open) {
      setLocationId(locations.length > 0 ? locations[0].id.toString() : '')
      setPositionId(positions.length > 0 ? positions[0].id.toString() : '')
      setBeginTime('09:00')
      setEndTime('17:00')
      setSpotsToFill(1)
      setNotes('')
      setSelectedDates([])
    }
  }, [open, shift, locations, positions])

  const handleSubmit = async () => {
    // Name the field that's actually missing — a generic message is useless
    // when the real cause is that the event has no locations configured.
    const missing: string[] = []
    if (!locationId) missing.push('a location')
    if (!positionId) missing.push('a position')
    if (selectedDates.length === 0) missing.push('at least one date')
    if (missing.length > 0) {
      toast.error(`Please select ${missing.join(', ')}`)
      return
    }

    setSaving(true)
    try {
      await onSave({
        locationId: parseInt(locationId),
        positionId: parseInt(positionId),
        beginTime: DateTime.fromFormat(beginTime, 'HH:mm').toJSDate(),
        endTime: DateTime.fromFormat(endTime, 'HH:mm').toJSDate(),
        spotsToFill,
        recurrenceDates: selectedDates.map((d) =>
          DateTime.fromJSDate(d).set({ hour: 12 }).toJSDate()
        ),
        notes,
      })
    } catch {
      // handled by parent
    } finally {
      setSaving(false)
    }
  }

  // Compute shift length
  const shiftLength = (() => {
    try {
      const b = DateTime.fromFormat(beginTime, 'HH:mm')
      let e = DateTime.fromFormat(endTime, 'HH:mm')
      if (e < b) e = e.plus({ days: 1 })
      const diff = e.diff(b, 'hours').hours
      return diff > 0 ? `${diff}h` : ''
    } catch {
      return ''
    }
  })()

  // A shift must reference one location and one position, so the form is
  // unusable until the event has at least one of each.
  const missingPrereqs: SetupStepId[] = [
    ...(locations.length === 0 ? (['locations'] as SetupStepId[]) : []),
    ...(positions.length === 0 ? (['positions'] as SetupStepId[]) : []),
  ]

  if (missingPrereqs.length > 0) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{shift ? 'Edit Shift' : 'Create Shift'}</DialogTitle>
            <DialogDescription>
              This event isn't ready for shifts yet
            </DialogDescription>
          </DialogHeader>
          <SetupPrerequisiteAlert
            eventId={eventId}
            missing={missingPrereqs}
            action="create a shift"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{shift ? 'Edit Shift' : 'Create Shift'}</DialogTitle>
          <DialogDescription>
            {shift
              ? 'Modify shift details'
              : 'Set up a new shift with location, time, and dates'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Location *</Label>
              <Select value={locationId} onValueChange={setLocationId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id.toString()}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Position *</Label>
              <Select value={positionId} onValueChange={setPositionId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select position" />
                </SelectTrigger>
                <SelectContent>
                  {positions.map((p) => (
                    <SelectItem key={p.id} value={p.id.toString()}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Start Time *</Label>
              <Select value={beginTime} onValueChange={setBeginTime}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeOptions.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>End Time *</Label>
              <Select value={endTime} onValueChange={setEndTime}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeOptions.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                # Staff{' '}
                {shiftLength && (
                  <span className="text-muted-foreground">({shiftLength})</span>
                )}
              </Label>
              <Input
                type="number"
                min={1}
                value={spotsToFill}
                onChange={(e) => setSpotsToFill(parseInt(e.target.value) || 1)}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional shift notes"
            />
          </div>
          <div className="space-y-2">
            <Label>
              Dates *{' '}
              <span className="text-muted-foreground text-xs">
                ({selectedDates.length} selected)
              </span>
            </Label>
            <div className="flex justify-center border rounded-md p-2">
              <CalendarPicker
                mode="multiple"
                selected={selectedDates}
                onSelect={(dates) => setSelectedDates(dates || [])}
                disabled={[
                  ...(eventStart ? [{ before: eventStart }] : []),
                  ...(eventEnd ? [{ after: eventEnd }] : []),
                ]}
                defaultMonth={eventStart}
                numberOfMonths={daysInEvent.length > 31 ? 2 : 1}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving...' : shift ? 'Save Changes' : 'Create Shift'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── Schedule List View ──────────────────────────────────────────────────

interface ListRow {
  spotId: number
  shiftId: number
  date: string
  beginTime: string
  endTime: string
  locationName: string
  positionName: string
  spot: ShiftSpot
  shift: Shift
  recurrence: ShiftRecurrence
}

type ListSortKey =
  | 'date'
  | 'start'
  | 'end'
  | 'location'
  | 'position'
  | 'assigned'
  | 'status'

// Human-readable status label for a spot, also used as the sort key for the
// Status column.
const spotStatusLabel = (spot: ShiftSpot): string => {
  if (spot.state === ShiftSpotState.UNASSIGNED) return 'Open'
  if (spot.state === ShiftSpotState.UNASSIGNABLE) return 'N/A'
  switch (spot.punchClockState) {
    case ShiftSpotPunchClockState.CHECKED_IN:
      return 'Checked In'
    case ShiftSpotPunchClockState.BREAK:
      return 'On Break'
    case ShiftSpotPunchClockState.CHECKED_OUT:
      return 'Checked Out'
    case ShiftSpotPunchClockState.LATE:
    case ShiftSpotPunchClockState.MISSED:
      return 'Late/Missed'
    case ShiftSpotPunchClockState.ERROR:
      return 'Error'
    default:
      return 'Assigned'
  }
}

interface ScheduleListViewProps {
  schedule: EventSchedule
  locationFilter: string
  /** Selected position ids, or null for "All Positions" */
  positionFilterIds: Set<number> | null
  startTimeFilter: string
  spotStatusFilter: string
  identityFilter: string
  onUnassign: (spotId: number) => Promise<void>
  onAssignClick: (
    spot: ShiftSpot,
    shift: Shift,
    recurrence: ShiftRecurrence
  ) => void
  onMarkUnassignable: (spotId: number) => Promise<void>
  onMarkAvailable: (spotId: number) => Promise<void>
  onSpotClick: (
    spot: ShiftSpot,
    shift: Shift,
    recurrence: ShiftRecurrence
  ) => void
  onBulkAssign: (spotIds: number[]) => void
  onBulkClockAsScheduled: (spotIds: number[]) => Promise<void>
  onPrintEmail: (spotIds: number[], includeTimeFields: boolean, includeBreakFields: boolean) => void
  onEmail: (
    spotIds: number[],
    includeTimeFields: boolean,
    includeBreakFields: boolean
  ) => Promise<{ content: string; signature: string } | null>
  onGenerateEmployeeReport: (
    spotIds: number[]
  ) => Promise<{ content: string; signature: string }>
}

const ScheduleListView: React.FC<ScheduleListViewProps> = ({
  schedule,
  locationFilter,
  positionFilterIds,
  startTimeFilter,
  spotStatusFilter,
  identityFilter,
  onUnassign,
  onAssignClick,
  onMarkUnassignable,
  onMarkAvailable,
  onSpotClick,
  onBulkAssign,
  onBulkClockAsScheduled,
  onPrintEmail,
  onEmail,
  onGenerateEmployeeReport,
}) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [bulkLoading, setBulkLoading] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false)
  const [emailDialog, setEmailDialog] = useState<{
    open: boolean
    content: string
    signature: string
    defaultRecipients: string
  }>({ open: false, content: '', signature: '', defaultRecipients: '' })

  const triggerEmail = async (
    includeTimeFields: boolean,
    includeBreakFields: boolean
  ) => {
    const result = await onEmail([...selectedIds], includeTimeFields, includeBreakFields)
    if (result) {
      // If every selected spot is assigned to the same identity, prefill that
      // person's email so the user doesn't have to look it up.
      const fulfilledBys = rows
        .filter((r) => selectedIds.has(r.spotId))
        .map((r) => r.spot.fulfilledBy)
        .filter((i): i is NonNullable<typeof i> => !!i)
      const uniqueIds = new Set(fulfilledBys.map((i) => i.id))
      const defaultRecipients =
        uniqueIds.size === 1 && fulfilledBys[0]?.email
          ? fulfilledBys[0].email
          : ''
      setEmailDialog({
        open: true,
        content: result.content,
        signature: result.signature,
        defaultRecipients,
      })
    }
  }
  const [fromDate, setFromDate] = useState<Date | undefined>(
    schedule.startDate ? parseLocalDate(schedule.startDate) : undefined
  )
  const [toDate, setToDate] = useState<Date | undefined>(
    schedule.endDate ? parseLocalDate(schedule.endDate) : undefined
  )
  const [currentPage, setListPage] = useState(1)
  const [pageSize, setListPageSize] = useState(50)
  const [sortKey, setSortKey] = useState<ListSortKey>('date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')

  const handleSort = (key: ListSortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const renderSortHead = (
    key: ListSortKey,
    label: string,
    className?: string
  ) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => handleSort(key)}
        className="inline-flex items-center gap-1 select-none hover:text-foreground"
      >
        {label}
        {sortKey === key ? (
          sortDir === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )
        ) : (
          <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />
        )}
      </button>
    </TableHead>
  )

  // Reset page and clear selection when filters change so bulk actions
  // don't operate on shifts that are no longer visible
  useEffect(() => {
    setListPage(1)
    setSelectedIds(new Set())
  }, [
    spotStatusFilter,
    startTimeFilter,
    identityFilter,
    locationFilter,
    positionFilterIds,
    fromDate,
    toDate,
  ])

  const rows = useMemo(() => {
    const result: ListRow[] = []
    schedule.shifts
      .filter(
        (s) =>
          locationFilter === 'all' ||
          s.location?.id === parseInt(locationFilter)
      )
      .filter(
        (s) =>
          !positionFilterIds ||
          (s.position != null && positionFilterIds.has(s.position.id))
      )
      .filter(
        (s) => startTimeFilter === 'all' || s.beginTime === startTimeFilter
      )
      .forEach((shift) => {
        shift.recurrences.forEach((recurrence) => {
          ;[...recurrence.spots]
            .sort((a, b) => a.id - b.id)
            .forEach((spot) => {
              result.push({
                spotId: spot.id,
                shiftId: shift.id,
                date: recurrence.date,
                beginTime: shift.beginTime,
                endTime: shift.endTime,
                locationName: shift.location?.name || '-',
                positionName: shift.position?.name || '-',
                spot,
                shift,
                recurrence,
              })
            })
        })
      })
    return result.sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.beginTime.localeCompare(b.beginTime) ||
        a.locationName.localeCompare(b.locationName) ||
        a.spotId - b.spotId
    )
  }, [schedule, locationFilter, positionFilterIds, startTimeFilter])

  const filteredRows = useMemo(() => {
    let result = rows

    // Date range filter
    if (fromDate) {
      const from = DateTime.fromJSDate(fromDate).startOf('day')
      result = result.filter((r) => DateTime.fromISO(r.date) >= from)
    }
    if (toDate) {
      const to = DateTime.fromJSDate(toDate).endOf('day')
      result = result.filter((r) => DateTime.fromISO(r.date) <= to)
    }

    if (spotStatusFilter !== 'all') {
      result = result.filter((r) => matchesSpotStatus(r.spot, spotStatusFilter))
    }

    if (identityFilter !== 'all') {
      const targetId = parseInt(identityFilter)
      result = result.filter((r) => r.spot.fulfilledBy?.id === targetId)
    }

    return result
  }, [rows, spotStatusFilter, identityFilter, fromDate, toDate])

  const sortedRows = useMemo(() => {
    const getVal = (r: ListRow): string => {
      switch (sortKey) {
        case 'date':
          return r.date
        case 'start':
          return r.beginTime
        case 'end':
          return r.endTime
        case 'location':
          return r.locationName.toLowerCase()
        case 'position':
          return r.positionName.toLowerCase()
        case 'assigned':
          // Unassigned spots sort last regardless of direction handling below.
          return r.spot.fulfilledBy
            ? `${r.spot.fulfilledBy.lastName} ${r.spot.fulfilledBy.firstName}`.toLowerCase()
            : '~'
        case 'status':
          return spotStatusLabel(r.spot)
        default:
          return ''
      }
    }
    return [...filteredRows].sort((a, b) => {
      const cmp = getVal(a).localeCompare(getVal(b)) || a.spotId - b.spotId
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [filteredRows, sortKey, sortDir])

  const totalItems = sortedRows.length
  const totalPages = Math.ceil(totalItems / pageSize)
  const startIndex = (currentPage - 1) * pageSize
  const paginatedRows = sortedRows.slice(startIndex, startIndex + pageSize)

  // Select-all covers every row matching the current filters, not just the
  // visible page — selection is cleared whenever a filter changes, so the
  // selected set never contains rows the user can no longer see.
  const allFilteredSelected = sortedRows.length > 0 && sortedRows.every(r => selectedIds.has(r.spotId))
  const someFilteredSelected = selectedIds.size > 0

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(sortedRows.map(r => r.spotId)))
    }
  }

  const selectedRows = filteredRows.filter(r => selectedIds.has(r.spotId))

  // Group the selection by assignee so each person can be emailed a report
  // covering only their own spots — that is what makes the backend stamp the
  // employee name and POS PIN on the report. Unassigned spots have nobody to
  // send to; assignees without an email address are surfaced as skipped rather
  // than dropped silently.
  const { employeeEmailTargets, employeesWithoutEmail } = useMemo(() => {
    const byIdentity = new Map<number, BulkEmailTarget>()
    const noEmail = new Map<number, SimpleIdentity>()
    selectedRows.forEach((r) => {
      const identity = r.spot.fulfilledBy
      if (!identity) return
      if (!identity.email) {
        noEmail.set(identity.id, identity)
        return
      }
      const existing = byIdentity.get(identity.id)
      if (existing) existing.spotIds.push(r.spotId)
      else byIdentity.set(identity.id, { identity, spotIds: [r.spotId] })
    })
    const byName = (a: { identity: SimpleIdentity }, b: { identity: SimpleIdentity }) =>
      `${a.identity.lastName} ${a.identity.firstName}`.localeCompare(
        `${b.identity.lastName} ${b.identity.firstName}`
      )
    return {
      employeeEmailTargets: [...byIdentity.values()].sort(byName),
      employeesWithoutEmail: [...noEmail.values()].sort((a, b) =>
        byName({ identity: a }, { identity: b })
      ),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, selectedIds])

  const selectedUnassigned = selectedRows.filter(r => r.spot.state === ShiftSpotState.UNASSIGNED)
  const selectedAssigned = selectedRows.filter(r => r.spot.state === ShiftSpotState.ASSIGNED)
  const selectedUnassignable = selectedRows.filter(r => r.spot.state === ShiftSpotState.UNASSIGNABLE)

  // Bulk unassign is destructive and can span every page of the filtered set,
  // so it is gated behind a dialog that makes the user type the exact count.
  // The rows are snapshotted when the dialog opens: spots flip out of ASSIGNED
  // as the batch runs, so deriving the count live would make it tick down to 0
  // underneath the user mid-confirmation.
  const [pendingUnassign, setPendingUnassign] = useState<ListRow[] | null>(null)
  const [unassignConfirmText, setUnassignConfirmText] = useState('')

  const unassignRows = pendingUnassign ?? []
  const unassignCount = unassignRows.length
  const unassignEmployeeCount = new Set(
    unassignRows.map(r => r.spot.fulfilledBy?.id).filter(Boolean)
  ).size
  const unassignDates = unassignRows.map(r => r.date).sort()
  const unassignConfirmed = unassignConfirmText.trim() === String(unassignCount)

  const closeUnassignConfirm = () => {
    setPendingUnassign(null)
    setUnassignConfirmText('')
  }

  const handleBulkUnassign = async () => {
    if (!unassignConfirmed || !pendingUnassign) return
    const rows = pendingUnassign
    setBulkLoading(true)
    try {
      for (const row of rows) {
        await onUnassign(row.spotId)
      }
      setSelectedIds(new Set())
      closeUnassignConfirm()
      toast.success(`Unassigned ${rows.length} spot(s)`)
    } catch (error) { toast.error(extractErrorMessage(error, 'Failed to unassign some spots')) }
    finally { setBulkLoading(false) }
  }

  const handleBulkMarkUnassignable = async () => {
    setBulkLoading(true)
    try {
      for (const row of selectedUnassigned) {
        await onMarkUnassignable(row.spotId)
      }
      setSelectedIds(new Set())
      toast.success(`Marked ${selectedUnassigned.length} spot(s) unavailable`)
    } catch (error) { toast.error(extractErrorMessage(error, 'Failed to mark some spots')) }
    finally { setBulkLoading(false) }
  }

  const handleBulkMarkAvailable = async () => {
    setBulkLoading(true)
    try {
      for (const row of selectedUnassignable) {
        await onMarkAvailable(row.spotId)
      }
      setSelectedIds(new Set())
      toast.success(`Marked ${selectedUnassignable.length} spot(s) available`)
    } catch (error) { toast.error(extractErrorMessage(error, 'Failed to mark some spots')) }
    finally { setBulkLoading(false) }
  }

  return (
    <div className="space-y-4">
      {/* List filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">
                From
              </Label>
              <DatePicker
                date={fromDate}
                onDateChange={setFromDate}
                placeholder="Start date"
                dateFormat="MMM dd, yyyy"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-muted-foreground whitespace-nowrap">
                To
              </Label>
              <DatePicker
                date={toDate}
                onDateChange={setToDate}
                placeholder="End date"
                dateFormat="MMM dd, yyyy"
              />
              <Button
                variant="ghost"
                size="sm"
                disabled={!fromDate}
                onClick={() => setToDate(fromDate)}
              >
                Same as From
              </Button>
            </div>
            {(fromDate || toDate) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFromDate(
                    schedule.startDate
                      ? parseLocalDate(schedule.startDate)
                      : undefined
                  )
                  setToDate(
                    schedule.endDate
                      ? parseLocalDate(schedule.endDate)
                      : undefined
                  )
                }}
              >
                Reset Dates
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="text-sm px-3 py-1">
                  {selectedIds.size} selected
                  {allFilteredSelected && totalItems > paginatedRows.length
                    ? ' (all pages)'
                    : ''}
                </Badge>
                <div className="h-4 w-px bg-border" />
                {selectedUnassigned.length > 0 && (
                  <Button variant="outline" size="sm" onClick={() => onBulkAssign([...selectedIds])} disabled={bulkLoading}>
                    <User className="h-3.5 w-3.5 mr-1.5" />
                    Assign Employee ({selectedUnassigned.length})
                  </Button>
                )}
                {selectedAssigned.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setUnassignConfirmText('')
                      setPendingUnassign(selectedAssigned)
                    }}
                    disabled={bulkLoading}
                  >
                    <X className="h-3.5 w-3.5 mr-1.5" />
                    Unassign ({selectedAssigned.length})
                  </Button>
                )}
                {selectedUnassigned.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleBulkMarkUnassignable} disabled={bulkLoading}>
                    Mark Unavailable ({selectedUnassigned.length})
                  </Button>
                )}
                {selectedUnassignable.length > 0 && (
                  <Button variant="outline" size="sm" onClick={handleBulkMarkAvailable} disabled={bulkLoading}>
                    <Check className="h-3.5 w-3.5 mr-1.5" />
                    Mark Available ({selectedUnassignable.length})
                  </Button>
                )}
                {selectedAssigned.length > 0 && (
                  <>
                    <div className="h-4 w-px bg-border" />
                    <Button variant="outline" size="sm" onClick={async () => {
                      setBulkLoading(true)
                      try {
                        await onBulkClockAsScheduled([...selectedIds].filter(id => selectedAssigned.some(r => r.spotId === id)))
                        toast.success('Clocked in/out at scheduled times')
                        setSelectedIds(new Set())
                      } catch (error) { toast.error(extractErrorMessage(error, 'Failed to clock some spots')) }
                      finally { setBulkLoading(false) }
                    }} disabled={bulkLoading}>
                      <Clock className="h-3.5 w-3.5 mr-1.5" />
                      Clock as Scheduled ({selectedAssigned.length})
                    </Button>
                  </>
                )}
                <div className="h-4 w-px bg-border" />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={bulkLoading}>
                      <Printer className="h-3.5 w-3.5 mr-1.5" />
                      Print / Email
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => onPrintEmail([...selectedIds], false, false)}>
                      Print Schedule
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onPrintEmail([...selectedIds], true, false)}>
                      Print with Time Fields
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onPrintEmail([...selectedIds], false, true)}>
                      Print with Break Fields
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => triggerEmail(false, false)}>
                      Email Schedule
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => triggerEmail(true, false)}>
                      Email with Time Fields
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => triggerEmail(false, true)}>
                      Email with Break Fields
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setBulkEmailOpen(true)}
                      disabled={
                        employeeEmailTargets.length === 0 &&
                        employeesWithoutEmail.length === 0
                      }
                    >
                      Email Each Employee Separately
                      {employeeEmailTargets.length > 0
                        ? ` (${employeeEmailTargets.length})`
                        : ''}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setExportDialogOpen(true)}>
                      Custom Export...
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
                Clear
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List table */}
      <Card>
        <CardContent className="p-0">
          {totalItems === 0 ? (
            <div className="text-center py-12">
              <List className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">
                No shift spots match your filters
              </p>
            </div>
          ) : (
            <>
              <div className="px-4 py-2 text-xs text-muted-foreground border-b">
                <span className="font-semibold text-foreground">*</span> indicates the employee took a break during this shift
              </div>
              <table className="w-full caption-bottom text-sm">
                <TableHeader className="sticky top-14 z-20 shadow-[0_1px_3px_0_rgb(0,0,0,0.1)] [&_th]:bg-background">
                  <TableRow className="hover:bg-transparent border-b">
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={allFilteredSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label={`Select all ${totalItems} shift spots matching the current filters`}
                        title={`Select all ${totalItems} matching the current filters`}
                        className={someFilteredSelected && !allFilteredSelected ? 'data-[state=unchecked]:bg-primary/20' : ''}
                      />
                    </TableHead>
                    {renderSortHead('date', 'Date')}
                    {renderSortHead('start', 'Start')}
                    {renderSortHead('end', 'End')}
                    {renderSortHead('location', 'Location')}
                    {renderSortHead('position', 'Position')}
                    {renderSortHead('assigned', 'Assigned To')}
                    {renderSortHead('status', 'Status', 'text-center')}
                    <TableHead className="w-[50px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRows.map((row) => (
                    <TableRow
                      key={row.spotId}
                      className={cn(
                        'cursor-pointer',
                        row.spot.state === ShiftSpotState.UNASSIGNABLE && 'opacity-50',
                        selectedIds.has(row.spotId) && 'bg-primary/5'
                      )}
                      onClick={() => {
                        if (
                          row.spot.state === ShiftSpotState.ASSIGNED ||
                          row.spot.state === ShiftSpotState.UNASSIGNABLE
                        ) {
                          onSpotClick(row.spot, row.shift, row.recurrence)
                        }
                      }}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(row.spotId)}
                          onCheckedChange={() => toggleSelect(row.spotId)}
                          aria-label={`Select spot ${row.spotId}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">
                        {(() => {
                          try {
                            return DateTime.fromISO(row.date).toFormat(
                              'EEE MMM d, yyyy'
                            )
                          } catch {
                            return row.date
                          }
                        })()}
                      </TableCell>
                      <TableCell>{formatTime(row.beginTime)}</TableCell>
                      <TableCell>{formatTime(row.endTime)}</TableCell>
                      <TableCell>{row.locationName}</TableCell>
                      <TableCell>{row.positionName}</TableCell>
                      <TableCell>
                        {row.spot.fulfilledBy ? (
                          <Link
                            to={`/identity/${row.spot.fulfilledBy.id}`}
                            className="font-medium text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {row.spot.fulfilledBy.firstName}{' '}
                            {row.spot.fulfilledBy.lastName}
                            {row.spot.hadBreak ? '*' : ''}
                          </Link>
                        ) : row.spot.unassignable ? (
                          <span className="text-muted-foreground italic">
                            N/A
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.spot.state === ShiftSpotState.ASSIGNED &&
                          (() => {
                            const ps = row.spot.punchClockState
                            const label =
                              ps === ShiftSpotPunchClockState.CHECKED_IN
                                ? 'Checked In'
                                : ps === ShiftSpotPunchClockState.BREAK
                                  ? 'On Break'
                                  : ps === ShiftSpotPunchClockState.CHECKED_OUT
                                    ? 'Checked Out'
                                    : ps === ShiftSpotPunchClockState.LATE ||
                                        ps === ShiftSpotPunchClockState.MISSED
                                      ? 'Late/Missed'
                                      : ps === ShiftSpotPunchClockState.ERROR
                                        ? 'Error'
                                        : 'Assigned'
                            const colorClass =
                              ps === ShiftSpotPunchClockState.CHECKED_IN
                                ? 'bg-green-100 text-green-700'
                                : ps === ShiftSpotPunchClockState.BREAK
                                  ? 'bg-purple-100 text-purple-700'
                                  : ps === ShiftSpotPunchClockState.CHECKED_OUT
                                    ? 'bg-blue-100 text-blue-700'
                                    : ps === ShiftSpotPunchClockState.LATE ||
                                        ps === ShiftSpotPunchClockState.MISSED
                                      ? 'bg-amber-100 text-amber-700'
                                      : ps === ShiftSpotPunchClockState.ERROR
                                        ? 'bg-rose-100 text-rose-700'
                                        : 'bg-slate-100 text-slate-700'
                            return (
                              <Badge
                                variant="secondary"
                                className={cn('text-xs', colorClass)}
                              >
                                {label}
                              </Badge>
                            )
                          })()}
                        {row.spot.state === ShiftSpotState.UNASSIGNED && (
                          <Badge variant="secondary" className="text-xs">
                            Open
                          </Badge>
                        )}
                        {row.spot.state === ShiftSpotState.UNASSIGNABLE && (
                          <Badge
                            variant="secondary"
                            className="text-xs bg-red-50 text-red-700"
                          >
                            N/A
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="h-7 w-7 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {row.spot.state === ShiftSpotState.UNASSIGNED && (
                              <>
                                <DropdownMenuItem
                                  onClick={() =>
                                    onAssignClick(
                                      row.spot,
                                      row.shift,
                                      row.recurrence
                                    )
                                  }
                                >
                                  <User className="h-4 w-4 mr-2" />
                                  Assign
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => onMarkUnassignable(row.spotId)}
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Mark Unavailable
                                </DropdownMenuItem>
                              </>
                            )}
                            {row.spot.state === ShiftSpotState.ASSIGNED && (
                              <DropdownMenuItem
                                onClick={() => onUnassign(row.spotId)}
                              >
                                <X className="h-4 w-4 mr-2" />
                                Unassign
                              </DropdownMenuItem>
                            )}
                            {row.spot.state === ShiftSpotState.UNASSIGNABLE && (
                              <DropdownMenuItem
                                onClick={() => onMarkAvailable(row.spotId)}
                              >
                                <Check className="h-4 w-4 mr-2" />
                                Mark Available
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </table>
              <div className="flex items-center justify-between border-t px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>Rows per page</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(v) => {
                      setListPageSize(parseInt(v))
                      setListPage(1)
                    }}
                  >
                    <SelectTrigger className="h-8 w-[70px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                      <SelectItem value="250">250</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {startIndex + 1}-
                    {Math.min(startIndex + pageSize, totalItems)} of{' '}
                    {totalItems}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setListPage((p) => p - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setListPage((p) => p + 1)}
                      disabled={currentPage >= totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={pendingUnassign !== null}
        onOpenChange={(open) => {
          if (bulkLoading || open) return
          closeUnassignConfirm()
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Unassign {unassignCount} shift{unassignCount === 1 ? '' : 's'}?
            </DialogTitle>
            <DialogDescription className="text-left">
              This removes {unassignEmployeeCount === 1 ? 'the employee' : 'employees'} from{' '}
              {unassignCount} assigned shift spot{unassignCount === 1 ? '' : 's'}
              {unassignEmployeeCount > 0 && (
                <> across {unassignEmployeeCount} employee{unassignEmployeeCount === 1 ? '' : 's'}</>
              )}
              {unassignDates.length > 0 && (
                <>
                  {' '}from{' '}
                  {DateTime.fromISO(unassignDates[0]).toFormat('MMM d, yyyy')}
                  {unassignDates[0] !== unassignDates[unassignDates.length - 1] && (
                    <> to {DateTime.fromISO(unassignDates[unassignDates.length - 1]).toFormat('MMM d, yyyy')}</>
                  )}
                </>
              )}
              . Selected spots may span pages beyond the one you are viewing. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="unassign-confirm-count" className="text-sm">
              Type <span className="font-semibold text-foreground">{unassignCount}</span> to confirm
            </Label>
            <Input
              id="unassign-confirm-count"
              value={unassignConfirmText}
              onChange={(e) => setUnassignConfirmText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && unassignConfirmed && !bulkLoading) {
                  handleBulkUnassign()
                }
              }}
              placeholder={String(unassignCount)}
              autoFocus
              autoComplete="off"
              disabled={bulkLoading}
            />
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={closeUnassignConfirm}
              disabled={bulkLoading}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkUnassign}
              disabled={!unassignConfirmed || bulkLoading}
              className="flex items-center gap-2"
            >
              {bulkLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              {bulkLoading
                ? 'Unassigning...'
                : `Unassign ${unassignCount} shift${unassignCount === 1 ? '' : 's'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ScheduleExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        eventId={schedule.id}
        spotIds={[...selectedIds]}
        locationName={locationFilter === 'all' ? 'All' : 'Filtered'}
        positionName={positionFilterIds ? 'Filtered' : 'All'}
        fromDate={fromDate ? DateTime.fromJSDate(fromDate).toFormat('yyyy-MM-dd') : DateTime.fromJSDate(parseLocalDate(schedule.startDate)).toFormat('yyyy-MM-dd')}
        toDate={toDate ? DateTime.fromJSDate(toDate).toFormat('yyyy-MM-dd') : DateTime.fromJSDate(parseLocalDate(schedule.endDate)).toFormat('yyyy-MM-dd')}
      />

      <ScheduleEmailDialog
        open={emailDialog.open}
        onOpenChange={(open) => setEmailDialog((prev) => ({ ...prev, open }))}
        content={emailDialog.content}
        signature={emailDialog.signature}
        clientId={schedule.client?.id ?? null}
        defaultSubject={`Schedule for ${schedule.name || 'event'}`}
        defaultRecipients={emailDialog.defaultRecipients}
      />

      <BulkEmployeeEmailDialog
        open={bulkEmailOpen}
        onOpenChange={setBulkEmailOpen}
        targets={employeeEmailTargets}
        skipped={employeesWithoutEmail}
        clientId={schedule.client?.id ?? null}
        defaultSubject={`Your Schedule for ${schedule.name || 'event'}`}
        generateReport={(target) => onGenerateEmployeeReport(target.spotIds)}
      />
    </div>
  )
}

// ─── Shift Spot Detail Dialog ────────────────────────────────────────────

interface ShiftSpotDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  data: { spot: ShiftSpot; shift: Shift; recurrence: ShiftRecurrence } | null
  timezone: string
  onSave: (spotId: number, request: ShiftSpotRequest) => Promise<void>
  onUnassign: (spotId: number) => Promise<void>
  onMarkAvailable: (spotId: number) => Promise<void>
}

interface BreakState {
  id: number
  breakStart: string
  breakEnd: string
  breakStartContext?: IntervalTimeContext
  breakEndContext?: IntervalTimeContext
  // The punches these times came from, when they were scanned rather than
  // typed in. Only used for display.
  breakStartScanPunch?: PunchClockPunch
  breakEndScanPunch?: PunchClockPunch
}

let breakIdCounter = -1

// Break times have to land on a quarter-hour boundary (:00/:15/:30/:45), so
// anything the user sets gets snapped to the nearest one. The values are
// wall-clock "yyyy-MM-dd'T'HH:mm" strings, so parse them in UTC to keep the
// arithmetic free of DST shifts -- only the literal hour/minute matters here.
const BREAK_SNAP_MINUTES = 15

const roundToQuarterHour = (dt: DateTime): DateTime => {
  const truncated = dt.set({ second: 0, millisecond: 0 })
  const snappedMinute =
    Math.round(truncated.minute / BREAK_SNAP_MINUTES) * BREAK_SNAP_MINUTES
  return truncated.plus({ minutes: snappedMinute - truncated.minute })
}

const snapToQuarterHour = (value: string): string => {
  if (!value) return value
  const dt = DateTime.fromISO(value, { zone: 'utc' })
  if (!dt.isValid) return value
  return roundToQuarterHour(dt).toFormat("yyyy-MM-dd'T'HH:mm")
}

const ceilToQuarterHour = (dt: DateTime): DateTime => {
  const rounded = dt.set({ second: 0, millisecond: 0 })
  const remainder = rounded.minute % BREAK_SNAP_MINUTES
  const atBoundary = remainder === 0 && dt.second === 0 && dt.millisecond === 0
  return atBoundary
    ? rounded
    : rounded.plus({ minutes: BREAK_SNAP_MINUTES - remainder })
}

const floorToQuarterHour = (dt: DateTime): DateTime =>
  dt
    .set({ second: 0, millisecond: 0 })
    .minus({ minutes: dt.minute % BREAK_SNAP_MINUTES })

// Small icon next to each punch field showing whether the value came from
// the punch clock scanner or was manually entered on the site. Uses the
// native browser tooltip via `title` to explain on hover.
const PunchContextIndicator: React.FC<{ context?: IntervalTimeContext }> = ({
  context,
}) => {
  if (context === IntervalTimeContext.PUNCH_CLOCK) {
    return (
      <span
        title="Scanned by the punch clock"
        aria-label="Scanned by the punch clock"
        className="inline-flex"
      >
        <ScanLine className="h-3.5 w-3.5 text-blue-600" />
      </span>
    )
  }
  if (context === IntervalTimeContext.MANUAL) {
    return (
      <span
        title="Manually entered"
        aria-label="Manually entered"
        className="inline-flex"
      >
        <Edit className="h-3.5 w-3.5 text-amber-600" />
      </span>
    )
  }
  return null
}

// The punch clock stores two times per scan: `scanTime`, the moment the badge
// was actually scanned, and `punchTime`, that moment rounded to the payroll
// interval. Only the rounded one becomes the in/out or break time, so the
// dialog looks up the originating punch to show the real scan alongside it.
const punchMatchesIntervalTime = (
  punch: PunchClockPunch,
  value: Date | string | null | undefined,
  timeOnly: boolean
): boolean => {
  if (!value) return false
  const punchTime = DateTime.fromJSDate(new Date(punch.punchTime)).toUTC()
  if (!punchTime.isValid) return false
  // Break times come back as a UTC "HH:mm:ss" string rather than a full date.
  if (timeOnly) return punchTime.toFormat('HH:mm:ss') === String(value)
  return punchTime.toMillis() === DateTime.fromJSDate(new Date(value)).toMillis()
}

const findScanPunch = (
  punchClock: PunchClock | undefined,
  punchType: PunchType,
  intervalTime: Date | string | null | undefined,
  timeOnly = false
): PunchClockPunch | undefined => {
  const candidates = (punchClock?.punches || []).filter(
    (p) => p.punchType === punchType
  )
  if (candidates.length === 0) return undefined
  // Punches arrive sorted by scan time, so the newest matching one wins; fall
  // back to the newest of that type if nothing lines up exactly.
  const matched = [...candidates]
    .reverse()
    .find((p) => punchMatchesIntervalTime(p, intervalTime, timeOnly))
  return matched || candidates[candidates.length - 1]
}

const scanPunchForInterval = (
  punchClock: PunchClock | undefined,
  context: IntervalTimeContext | undefined,
  punchType: PunchType,
  intervalTime: Date | string | null | undefined,
  timeOnly = false
): PunchClockPunch | undefined => {
  if (context !== IntervalTimeContext.PUNCH_CLOCK) return undefined
  return findScanPunch(punchClock, punchType, intervalTime, timeOnly)
}

// The supervisor whose punch clock session issued the scan.
const scannedByName = (punch?: PunchClockPunch): string => {
  const supervisor = punch?.supervisor
  if (!supervisor) return ''
  return `${supervisor.firstName || ''} ${supervisor.lastName || ''}`.trim()
}

// Shown next to a rounded punch time to reveal the unrounded scan behind it.
const ScanTimeHint: React.FC<{ punch?: PunchClockPunch; timezone: string }> = ({
  punch,
  timezone,
}) => {
  if (!punch?.scanTime) return null
  const scanned = DateTime.fromJSDate(new Date(punch.scanTime)).setZone(timezone)
  if (!scanned.isValid) return null
  const by = scannedByName(punch)
  return (
    <span
      className="text-xs font-normal text-muted-foreground"
      title="Time the badge was actually scanned, before payroll rounding"
    >
      scanned {scanned.toFormat('h:mm:ss a')}
      {by ? ` by ${by}` : ''}
    </span>
  )
}

const ShiftSpotDetailDialog: React.FC<ShiftSpotDetailDialogProps> = ({
  open,
  onOpenChange,
  data,
  timezone: rawTimezone,
  onSave,
  onUnassign,
  onMarkAvailable,
}) => {
  // Final safety net: if `timezone` is ever falsy at runtime, we'd silently
  // fall back to the browser's local zone (Luxon `setZone(null)` semantics).
  // That's the bug we keep hitting, so refuse to do it: hard-fall to UTC and
  // log loudly so the upstream gap is visible.
  if (!rawTimezone && typeof console !== 'undefined') {
    console.warn(
      '[ShiftSpotDetailDialog] timezone prop missing; falling back to UTC. ' +
        'Check the schedule payload — schedule.timezone should be a resolved IANA id.'
    )
  }
  const timezone = rawTimezone || 'UTC'

  const [notes, setNotes] = useState('')
  const [inTime, setInTime] = useState('')
  const [outTime, setOutTime] = useState('')
  const [breaks, setBreaks] = useState<BreakState[]>([])
  const [saving, setSaving] = useState(false)
  // Break times as they arrived from the API, keyed by break id. Punch-clock
  // scans land on arbitrary minutes; we only snap the ones the user touches so
  // that saving an untouched spot doesn't quietly rewrite scanned times.
  const initialBreaksRef = useRef<
    Record<number, { breakStart: string; breakEnd: string }>
  >({})

  useEffect(() => {
    if (open && data) {
      setNotes(data.spot.notes || '')
      breakIdCounter = -1
      if (data.spot.punchClock) {
        const pc = data.spot.punchClock

        // Break checkIn/checkOut come from the API as "HH:mm:ss" strings
        // representing a UTC time-of-day (the JDBC TIME column is read with
        // the server's UTC JVM). Anchor them to the in-time's UTC calendar
        // date, then project into the event timezone for display.
        const anchorUtcDate = pc.inAndOut?.checkIn
          ? DateTime.fromJSDate(new Date(pc.inAndOut.checkIn))
              .toUTC()
              .toFormat('yyyy-MM-dd')
          : undefined
        const breakTimeStringToEventTz = (
          timeStr: string | null | undefined
        ): string => {
          if (!timeStr || !anchorUtcDate) return ''
          return DateTime.fromISO(`${anchorUtcDate}T${timeStr}`, {
            zone: 'utc',
          })
            .setZone(timezone)
            .toFormat("yyyy-MM-dd'T'HH:mm")
        }

        setInTime(
          pc.inAndOut?.checkIn
            ? DateTime.fromJSDate(new Date(pc.inAndOut.checkIn))
                .setZone(timezone)
                .toFormat("yyyy-MM-dd'T'HH:mm")
            : ''
        )
        setOutTime(
          pc.inAndOut?.checkOut
            ? DateTime.fromJSDate(new Date(pc.inAndOut.checkOut))
                .setZone(timezone)
                .toFormat("yyyy-MM-dd'T'HH:mm")
            : ''
        )
        const loadedBreaks = (pc.breaks || []).map((b) => ({
          id: b.id,
          breakStart: breakTimeStringToEventTz(b.checkIn),
          breakEnd: breakTimeStringToEventTz(b.checkOut),
          breakStartContext: b.checkInContext,
          breakEndContext: b.checkOutContext,
          breakStartScanPunch: scanPunchForInterval(
            pc,
            b.checkInContext,
            PunchType.BREAK_START,
            b.checkIn,
            true
          ),
          breakEndScanPunch: scanPunchForInterval(
            pc,
            b.checkOutContext,
            PunchType.BREAK_END,
            b.checkOut,
            true
          ),
        }))
        initialBreaksRef.current = Object.fromEntries(
          loadedBreaks.map((b) => [
            b.id,
            { breakStart: b.breakStart, breakEnd: b.breakEnd },
          ])
        )
        setBreaks(loadedBreaks)
      } else {
        setInTime('')
        setOutTime('')
        setBreaks([])
        initialBreaksRef.current = {}
      }
    }
  }, [open, data, timezone])

  if (!data) return null

  const { spot, shift, recurrence } = data
  const isAssigned = spot.state === ShiftSpotState.ASSIGNED
  const employee = spot.fulfilledBy

  const addBreak = () => {
    setBreaks((prev) => [
      ...prev,
      { id: --breakIdCounter, breakStart: '', breakEnd: '' },
    ])
  }

  const updateBreak = (
    id: number,
    field: 'breakStart' | 'breakEnd',
    value: string
  ) => {
    setBreaks((prev) =>
      prev.map((b) => (b.id === id ? { ...b, [field]: value } : b))
    )
  }

  // Snap once the field is done being edited, so typing an intermediate
  // minute value isn't fought mid-keystroke.
  const snapBreakField = (id: number, field: 'breakStart' | 'breakEnd') => {
    setBreaks((prev) =>
      prev.map((b) =>
        b.id === id ? { ...b, [field]: snapToQuarterHour(b[field]) } : b
      )
    )
  }

  const removeBreak = (id: number) => {
    setBreaks((prev) => prev.filter((b) => b.id !== id))
  }

  const autoAddBreak = (durationMinutes: number) => {
    // Determine shift boundaries (all anchored in the event's timezone)
    const shiftDate = DateTime.fromISO(recurrence.date, { zone: timezone })
    const begin = DateTime.fromFormat(shift.beginTime, 'HH:mm:ss')
    const end = DateTime.fromFormat(shift.endTime, 'HH:mm:ss')

    const shiftStart = inTime
      ? DateTime.fromISO(inTime, { zone: timezone })
      : shiftDate.set({ hour: begin.hour, minute: begin.minute })
    let shiftEnd = outTime
      ? DateTime.fromISO(outTime, { zone: timezone })
      : shiftDate.set({ hour: end.hour, minute: end.minute })
    if (shiftEnd <= shiftStart) shiftEnd = shiftEnd.plus({ days: 1 })

    // Collect existing break intervals sorted by start
    const occupied = breaks
      .filter((b) => b.breakStart && b.breakEnd)
      .map((b) => ({
        start: DateTime.fromISO(b.breakStart, { zone: timezone }),
        end: DateTime.fromISO(b.breakEnd, { zone: timezone }),
      }))
      .sort((a, b) => a.start.toMillis() - b.start.toMillis())

    // Find work gaps
    const gaps: { start: DateTime; end: DateTime; len: number }[] = []
    let cursor = shiftStart
    for (const occ of occupied) {
      if (occ.start > cursor) {
        const len = occ.start.diff(cursor, 'minutes').minutes
        gaps.push({ start: cursor, end: occ.start, len })
      }
      if (occ.end > cursor) cursor = occ.end
    }
    if (shiftEnd > cursor) {
      const len = shiftEnd.diff(cursor, 'minutes').minutes
      gaps.push({ start: cursor, end: shiftEnd, len })
    }

    // Pick the largest gap
    if (gaps.length === 0) {
      toast.error('No available time for a break')
      return
    }
    const largest = gaps.reduce(
      (best, g) => (g.len > best.len ? g : best),
      gaps[0]
    )

    if (largest.len < durationMinutes) {
      toast.error(
        `Not enough room for a ${durationMinutes} minute break (largest gap is ${Math.floor(largest.len)}m)`
      )
      return
    }

    // Center the break in the largest gap, then pull it onto a quarter-hour
    // boundary without letting it slide outside the gap. Every offered
    // duration is a multiple of 15, so an aligned start keeps the end aligned.
    const gapMid = largest.start.plus({ minutes: largest.len / 2 })
    const earliestStart = ceilToQuarterHour(largest.start)
    const latestStart = floorToQuarterHour(
      largest.end.minus({ minutes: durationMinutes })
    )
    if (latestStart < earliestStart) {
      toast.error(
        `No ${durationMinutes} minute slot lands on a 15 minute boundary in the available time`
      )
      return
    }
    let breakStart = roundToQuarterHour(
      gapMid.minus({ minutes: durationMinutes / 2 })
    )
    if (breakStart < earliestStart) breakStart = earliestStart
    if (breakStart > latestStart) breakStart = latestStart
    const breakEnd = breakStart.plus({ minutes: durationMinutes })

    setBreaks((prev) => [
      ...prev,
      {
        id: --breakIdCounter,
        breakStart: breakStart.toFormat("yyyy-MM-dd'T'HH:mm"),
        breakEnd: breakEnd.toFormat("yyyy-MM-dd'T'HH:mm"),
      },
    ])
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const request: ShiftSpotRequest = {
        notes,
        punchClock: isAssigned
          ? {
              inTime: inTime
                ? DateTime.fromISO(inTime, { zone: timezone }).toJSDate()
                : (null as any),
              outTime: outTime
                ? DateTime.fromISO(outTime, { zone: timezone }).toJSDate()
                : (null as any),
              inTimeContext: IntervalTimeContext.MANUAL,
              outTimeContext: IntervalTimeContext.MANUAL,
              breaks: breaks.map((b) => {
                // Backstop for the blur snap: a value the user changed always
                // goes out on a quarter-hour boundary, whether or not the
                // field lost focus before Save was clicked. Values still
                // matching what the API sent are left untouched.
                const initial = initialBreaksRef.current[b.id]
                const snapIfEdited = (
                  value: string,
                  field: 'breakStart' | 'breakEnd'
                ) =>
                  value && value !== initial?.[field]
                    ? snapToQuarterHour(value)
                    : value
                const breakStart = snapIfEdited(b.breakStart, 'breakStart')
                const breakEnd = snapIfEdited(b.breakEnd, 'breakEnd')

                return {
                  id: b.id > 0 ? b.id : (null as any),
                  breakStart: breakStart
                    ? DateTime.fromISO(breakStart, { zone: timezone }).toJSDate()
                    : (null as any),
                  breakEnd: breakEnd
                    ? DateTime.fromISO(breakEnd, { zone: timezone }).toJSDate()
                    : (null as any),
                  breakStartContext: IntervalTimeContext.MANUAL,
                  breakEndContext: IntervalTimeContext.MANUAL,
                }
              }),
            }
          : null,
      }
      await onSave(spot.id, request)
    } catch {
      // handled by parent
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Shift Spot Details</DialogTitle>
          <DialogDescription>
            {formatTime(shift.beginTime)} - {formatTime(shift.endTime)} on{' '}
            {(() => {
              try {
                return DateTime.fromISO(recurrence.date, { zone: timezone })
                  .toFormat('EEE MMM d, yyyy')
              } catch {
                return recurrence.date
              }
            })()}
          </DialogDescription>
        </DialogHeader>

        {/* Shift Info */}
        <div className="grid grid-cols-4 gap-4 text-sm rounded-lg bg-muted/30 p-4">
          <div>
            <p className="text-muted-foreground text-xs mb-1">Location</p>
            <p className="font-medium">{shift.location?.name || '-'}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs mb-1">Position</p>
            <p className="font-medium">{shift.position?.name || '-'}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs mb-1">Assigned To</p>
            <p className="font-medium">
              {employee ? (
                <Link
                  to={`/identity/${employee.id}`}
                  className="text-primary hover:underline"
                >
                  {employee.firstName} {employee.lastName}
                </Link>
              ) : spot.unassignable ? (
                'Unavailable'
              ) : (
                'Unassigned'
              )}
            </p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs mb-1">Status</p>
            <div>
              {spot.punchClockState ? (
                <Badge
                  variant="secondary"
                  className={cn(
                    'text-xs',
                    punchClockSpotClass(spot.punchClockState)
                  )}
                >
                  {spot.punchClockState === ShiftSpotPunchClockState.CHECKED_IN
                    ? 'Checked In'
                    : spot.punchClockState === ShiftSpotPunchClockState.BREAK
                      ? 'On Break'
                      : spot.punchClockState ===
                          ShiftSpotPunchClockState.CHECKED_OUT
                        ? 'Checked Out'
                        : spot.punchClockState ===
                              ShiftSpotPunchClockState.LATE ||
                            spot.punchClockState ===
                              ShiftSpotPunchClockState.MISSED
                          ? 'Late/Missed'
                          : spot.punchClockState ===
                              ShiftSpotPunchClockState.ERROR
                            ? 'Error'
                            : 'Pending'}
                </Badge>
              ) : (
                <span className="text-sm">{spot.state}</span>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Spot Notes</Label>
            <Input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes for this assignment..."
              disabled={saving}
            />
            <p className="text-[11px] text-muted-foreground">
              Cleared if this employee is unassigned.
            </p>
          </div>
          <div className="space-y-2">
            <Label className="text-muted-foreground">Shift Notes</Label>
            <p className="text-sm bg-muted/30 rounded-md px-3 py-2 min-h-[36px]">
              {spot.shiftNotes || (
                <span className="text-muted-foreground italic">None</span>
              )}
            </p>
          </div>
        </div>

        {/* Punch Clock -- only for assigned spots */}
        {isAssigned && (
          <>
            <Separator />
            <div className="space-y-5">
              <div>
                <Label className="text-base font-semibold">Punch Clock</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  All times are in the event's time zone ({timezone || 'unknown'}).
                </p>
              </div>

              {/* In / Out times */}
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    In Time
                    <PunchContextIndicator
                      context={spot.punchClock?.inAndOut?.checkInContext}
                    />
                    <ScanTimeHint
                      punch={scanPunchForInterval(
                        spot.punchClock,
                        spot.punchClock?.inAndOut?.checkInContext,
                        PunchType.CHECK_IN,
                        spot.punchClock?.inAndOut?.checkIn
                      )}
                      timezone={timezone}
                    />
                  </Label>
                  <Input
                    type="datetime-local"
                    value={inTime}
                    onChange={(e) => setInTime(e.target.value)}
                    disabled={saving}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      const d = DateTime.fromISO(recurrence.date, { zone: timezone })
                      const begin = DateTime.fromFormat(
                        shift.beginTime,
                        'HH:mm:ss'
                      )
                      setInTime(
                        d
                          .set({ hour: begin.hour, minute: begin.minute })
                          .toFormat("yyyy-MM-dd'T'HH:mm")
                      )
                    }}
                    disabled={saving}
                  >
                    Set to Shift Start
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm flex items-center gap-2">
                    Out Time
                    <PunchContextIndicator
                      context={spot.punchClock?.inAndOut?.checkOutContext}
                    />
                    <ScanTimeHint
                      punch={scanPunchForInterval(
                        spot.punchClock,
                        spot.punchClock?.inAndOut?.checkOutContext,
                        PunchType.CHECK_OUT,
                        spot.punchClock?.inAndOut?.checkOut
                      )}
                      timezone={timezone}
                    />
                  </Label>
                  <Input
                    type="datetime-local"
                    value={outTime}
                    onChange={(e) => setOutTime(e.target.value)}
                    disabled={saving}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => {
                      const d = DateTime.fromISO(recurrence.date, { zone: timezone })
                      const end = DateTime.fromFormat(shift.endTime, 'HH:mm:ss')
                      let outDate = d.set({
                        hour: end.hour,
                        minute: end.minute,
                      })
                      const begin = DateTime.fromFormat(
                        shift.beginTime,
                        'HH:mm:ss'
                      )
                      if (end < begin) outDate = outDate.plus({ days: 1 })
                      setOutTime(outDate.toFormat("yyyy-MM-dd'T'HH:mm"))
                    }}
                    disabled={saving}
                  >
                    Set to Shift End
                  </Button>
                </div>
              </div>

              {/* Breaks */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm">Breaks</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Break times snap to 15 minute increments (:00, :15, :30,
                      :45).
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {[15, 30, 45, 60].map((mins) => (
                      <Button
                        key={mins}
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs px-2.5"
                        onClick={() => autoAddBreak(mins)}
                        disabled={saving}
                      >
                        {mins < 60 ? `${mins}m` : '1hr'}
                      </Button>
                    ))}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={addBreak}
                      disabled={saving}
                      className="h-7 text-xs px-2.5"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Custom
                    </Button>
                  </div>
                </div>
                {breaks.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic py-2">
                    No breaks registered.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {breaks.map((brk) => (
                      <div
                        key={brk.id}
                        className="grid grid-cols-[1fr_1fr_auto] items-end gap-3 p-3 rounded-lg bg-muted/30"
                      >
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground flex items-center gap-2">
                            Break Start
                            <PunchContextIndicator
                              context={brk.breakStartContext}
                            />
                            <ScanTimeHint
                              punch={brk.breakStartScanPunch}
                              timezone={timezone}
                            />
                          </Label>
                          <Input
                            type="datetime-local"
                            step={BREAK_SNAP_MINUTES * 60}
                            value={brk.breakStart}
                            onChange={(e) =>
                              updateBreak(brk.id, 'breakStart', e.target.value)
                            }
                            onBlur={() => snapBreakField(brk.id, 'breakStart')}
                            disabled={saving}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs text-muted-foreground flex items-center gap-2">
                            Break End
                            <PunchContextIndicator
                              context={brk.breakEndContext}
                            />
                            <ScanTimeHint
                              punch={brk.breakEndScanPunch}
                              timezone={timezone}
                            />
                          </Label>
                          <Input
                            type="datetime-local"
                            step={BREAK_SNAP_MINUTES * 60}
                            value={brk.breakEnd}
                            onChange={(e) =>
                              updateBreak(brk.id, 'breakEnd', e.target.value)
                            }
                            onBlur={() => snapBreakField(brk.id, 'breakEnd')}
                            disabled={saving}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-destructive hover:text-destructive"
                          onClick={() => removeBreak(brk.id)}
                          disabled={saving}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions row */}
              {(inTime || outTime || breaks.length > 0) && (
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => {
                      setInTime('')
                      setOutTime('')
                      setBreaks([])
                    }}
                    disabled={saving}
                  >
                    Reset All Punch Clock Data
                  </Button>
                </div>
              )}

              {/* Punch history */}
              {spot.punchClock?.punches &&
                spot.punchClock.punches.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">
                      Punch History
                    </Label>
                    <div className="border rounded-lg divide-y text-sm">
                      {spot.punchClock.punches.map((punch) => (
                        <div key={punch.id} className="px-4 py-2">
                          <div className="flex items-center justify-between gap-3">
                            <span className="font-medium">
                              {PunchTypeFriendlyName(punch.punchType)}
                            </span>
                            <span className="text-right text-muted-foreground">
                              {DateTime.fromJSDate(new Date(punch.punchTime))
                                .setZone(timezone)
                                .toFormat('MMM d, h:mm a')}
                              {punch.scanTime && (
                                <span
                                  className="block text-xs"
                                  title="Time the badge was actually scanned, before payroll rounding"
                                >
                                  scanned{' '}
                                  {DateTime.fromJSDate(new Date(punch.scanTime))
                                    .setZone(timezone)
                                    .toFormat('h:mm:ss a')}
                                  {scannedByName(punch)
                                    ? ` by ${scannedByName(punch)}`
                                    : ''}
                                </span>
                              )}
                            </span>
                          </div>
                          {punch.notes && (
                            <p className="text-xs text-muted-foreground mt-1 whitespace-pre-wrap break-words">
                              {punch.notes}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </>
        )}

        <DialogFooter className="pt-2">
          <div className="flex w-full justify-between">
            <div>
              {isAssigned && (
                <Button
                  variant="outline"
                  className="text-destructive hover:text-destructive"
                  onClick={() => onUnassign(spot.id)}
                  disabled={saving}
                >
                  Unassign
                </Button>
              )}
              {spot.state === ShiftSpotState.UNASSIGNABLE && (
                <Button
                  variant="outline"
                  onClick={() => onMarkAvailable(spot.id)}
                  disabled={saving}
                >
                  Mark Available
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
